import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  // Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select, Switch,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, getGridStringOperators } from '@mui/x-data-grid';
import { CheckCircle2, ChevronDown, ChevronUp, FileX2, Info, KeyRound, Pencil, PlusSquare, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { decodeJwtPayload } from '../helpers/accessApi';
import {
  applyRequestRow,
  fetchRequestPendingCounts,
  fetchUsuariosLocationSuggestions,
} from '../helpers/sheetsApi';
import {
  fetchPendingUserRequests,
  fetchRequestRoleOptions,
  requestDeleteUser,
  requestMoveUser,
  requestNewUser,
  requestRoleChange,
  requestUpdateUserInfo,
} from '../helpers/userRequestsApi';
import { fetchUserPasswordStatuses } from '../helpers/userPasswordsApi';
import {
  compareDateValues,
  formatDateTimesInText,
  formatDateTimeInputValue,
  formatDateValueForLabel,
  formatHumanDateTime,
  isDateLikeColumnLabel,
} from '../helpers/dateTime';
import {
  LOCATION_HEADERS,
  applyIndexedLocationsToRowData,
  buildLocationCellFilterText,
  findMatchingLocationScope,
  formatLocationScopeLabel,
  formatLocationScopeTooltip,
  getIndexedLocationsFromRow,
  getLocationColumnIndexes,
  rowContainsLocationScope,
  summarizeLocationCell,
} from '../helpers/locationScopes';
import UserPasswordsAdmin from './UserPasswordsAdmin';
import RequestChangeComparison from '../components/RequestChangeComparison';
import PendingRequestsOverview from '../components/PendingRequestsOverview';
import PhoneTextField from '../components/PhoneTextField';
import LocationStatusPanel from '../components/LocationStatusPanel';
import AdminLocationStatusEditor from '../components/AdminLocationStatusEditor';
import UserDataReview from '../components/UserDataReview';
import { LocationStatusFilterInput, LocationStatusQuickFilter } from '../components/LocationStatusFilter';
import { updateUserLocationStatus } from '../helpers/locationStatusApi';
import { createUserLocationStatusIndex, userReviewEmailKey } from '../helpers/userLocationStatus';
import { getSelectedLocationStatusChanges, locationStatusKey, saveUserWithLocationStatuses } from '../helpers/adminLocationStatus';
import { useLocationStatuses } from '../helpers/useLocationStatuses';
import { getUserLocationStatusDisplay } from '../helpers/locationStatusDisplay';
import {
  LOCATION_REVIEW_STATUS_FIELD,
  createLocationReviewIndex,
  doesLocationReviewMatchFilter,
  getLocationReviewFilterLabel,
  getRelevantLocationReviewScopes,
  getUserLocationReviewValue,
} from '../helpers/locationStatusFilters';
import { getSavedRequestFeedback } from '../helpers/requestFeedback';
import { useSheetsDashboard } from '../helpers/useSheetsDashboard';
import { createSheetCache } from '../helpers/sheetCache';
import { formatSheetDisplayText, getColumnDisplayName, getSheetDisplayName } from '../helpers/sheetDisplay';
import { ROLE_NOTIFICATION_SETTINGS, getRoleNotificationSetting } from '../helpers/roleNotificationSettings';
import { formatPhoneValueForRow, isPhoneColumnLabel } from '../helpers/phoneDisplay';
import { createGridSearchIndex, doesGridCellMatchFilter, doesGridSearchMatch, filterPhoneValueOptions, getGridSearchTerms } from '../helpers/gridFilters';
import { normalizeCellValue } from '../helpers/dataNormalization';

function normalizeUserRequestInput(header, value, country = '', input = false) {
  return normalizeCellValue(header, value, {
    sheetName: 'Usuarios', headers: ['Pais'], row: [country], input,
  });
}

const REQUEST_SHEET_NAMES = [
  'Solicitudes - Nuevo Usuario',
  'Solicitudes - Cambio de Rol',
  'Solicitudes - Eliminar Usuario',
  'Solicitudes - Mover Usuario',
  'Solicitudes - Datos Personales',
];
const REQUEST_DIALOG_SECTIONS = Object.freeze({
  ROLE_CHANGE: 'roleChange',
  MOVE_USER: 'moveUser',
  UPDATE_USER_INFO: 'updateUserInfo',
  DELETE_USER: 'deleteUser',
});
const REQUEST_NEW_USER_SHEET_NAME = 'Solicitudes - Nuevo Usuario';
const REQUEST_SUBMITTED_AT_HEADER = 'Fecha de solicitud';
const REQUEST_REQUESTER_HEADER_PREFIX = 'solicitado por -';
const REQUEST_TARGET_HEADER_PREFIX = 'usuario -';
const REQUEST_CURRENT_VALUE_SUFFIX = ' actual';
const REQUEST_VISIBLE_TARGET_HEADERS = new Set(['usuario - email', 'usuario - nombre', 'usuario - apellido']);
const ROLES_SHEET_NAME = 'Roles';
const CHURCH_STATE_SHEET_NAME = 'Iglesia - Estado';
const CHURCH_CITY_SHEET_NAME = 'Iglesia - Ciudad';
const CHURCH_NAME_SHEET_NAME = 'Iglesia - Nombre';
const COUNTRY_SHEET_NAME = 'Pais';
const LANGUAGE_SHEET_NAME = 'Idioma';
const USER_PASSWORDS_SHEET_NAME = 'contrasenas_acceso_usuarios';
const REFERENCE_SHEET_NAMES = [
  CHURCH_STATE_SHEET_NAME,
  CHURCH_CITY_SHEET_NAME,
  CHURCH_NAME_SHEET_NAME,
  COUNTRY_SHEET_NAME,
  LANGUAGE_SHEET_NAME,
];
const EMPTY_LOCATION_ENTRY = Object.freeze({
  index: 0,
  state: '',
  city: '',
  churchName: '',
  country: '',
});
const OPTIONAL_USUARIOS_FIELD_NAMES = new Set(['iglesia - nombre', 'telefono', 'idioma']);
const CREADO_HEADER = 'Creado';
const HIDDEN_USUARIOS_UI_HEADER_NAMES = new Set(['creado']);
const HIDDEN_USUARIOS_UI_DEFAULT_VALUES = Object.freeze({
  creado: 'No',
});
const QUICK_HIDE_LOCATION_HEADERS = new Set([
  normalizeHeaderName('Iglesia - Estado'),
  normalizeHeaderName('Iglesia - Ciudad'),
  normalizeHeaderName('Iglesia - Nombre'),
  normalizeHeaderName('Pais'),
]);
const REGULAR_USER_DEFAULT_HIDDEN_LOCATION_HEADERS = new Set([
  normalizeHeaderName('Iglesia - Estado'),
  normalizeHeaderName('Iglesia - Ciudad'),
  normalizeHeaderName('Iglesia - Nombre'),
  normalizeHeaderName('Pais'),
]);
const usuariosFilterAutocomplete = createFilterOptions({
  limit: 100,
  trim: true,
});
const phoneFilterAutocomplete = (options, { inputValue }) => filterPhoneValueOptions(options, inputValue);
const EMPTY_GRID_FILTER_MODEL = { items: [] };
const EMPTY_LOCATION_REQUESTS = [];
const GRID_FILTER_OPERATORS_WITHOUT_VALUE = new Set(['isEmpty', 'isNotEmpty']);
const GRID_STRING_OPERATOR_LABELS = Object.fromEntries(
  getGridStringOperators().map((operator) => [operator.value, operator.label || operator.value])
);

function getPrimaryGridFilterItem(model) {
  return Array.isArray(model?.items) ? (model.items[0] || null) : null;
}

function normalizeGridFilterComparisonValue(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? '').trim());
  }

  return String(value ?? '').trim();
}

function areGridFilterItemsEquivalent(leftItem, rightItem) {
  if (!leftItem && !rightItem) {
    return true;
  }

  if (!leftItem || !rightItem) {
    return false;
  }

  return (
    String(leftItem.field || '') === String(rightItem.field || '')
    && String(leftItem.operator || '') === String(rightItem.operator || '')
    && JSON.stringify(normalizeGridFilterComparisonValue(leftItem.value))
      === JSON.stringify(normalizeGridFilterComparisonValue(rightItem.value))
  );
}

function getPasswordStatusSummary(passwordStatus) {
  const isExpired = Boolean(passwordStatus?.isExpired);
  const expiresAt = String(passwordStatus?.expiresAt || '').trim();
  const createdAt = String(passwordStatus?.createdAt || '').trim();
  const updatedAt = String(passwordStatus?.updatedAt || '').trim();

  if (!passwordStatus?.hasPassword) {
    return {
      hasPassword: false,
      isExpired: false,
      buttonColor: 'default',
      tooltip: 'Gestionar contraseña. La contraseña se enviará automáticamente al correo del usuario y solo le dará acceso al portal de usuarios para guardar solicitudes de cambio de rol o sede. Esta contraseña no permite entrar al dashboard de administración ni hacer cambios directos en la hoja de Usuarios; cualquier cambio deberá hacerse mediante solicitudes aprobadas por un administrador.',
    };
  }

  const parts = [];

  if (isExpired) {
    parts.push('Este usuario tiene una contraseña vencida.');
  } else {
    parts.push('Este usuario ya tiene contraseña.');
  }

  if (expiresAt) {
    parts.push(`${isExpired ? 'Expiró' : 'Expira'} el ${formatHumanDateTime(expiresAt, expiresAt)}.`);
  }

  if (createdAt) {
    parts.push(`Creada el ${formatHumanDateTime(createdAt, createdAt)}.`);
  }

  if (updatedAt) {
    parts.push(`Última actualización: ${formatHumanDateTime(updatedAt, updatedAt)}.`);
  }

  return {
    hasPassword: true,
    isExpired,
    buttonColor: isExpired ? 'error' : 'warning',
    tooltip: parts.join(' '),
  };
}

function isGridFilterValuePresent(value) {
  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim() !== '');
  }

  return String(value ?? '').trim() !== '';
}

function isGridFilterItemActive(item) {
  if (!item?.field || !item?.operator) {
    return false;
  }

  if (GRID_FILTER_OPERATORS_WITHOUT_VALUE.has(item.operator)) {
    return true;
  }

  return isGridFilterValuePresent(item.value);
}

function getGridHeaderLabel(field, headers, sheetName) {
  if (field === LOCATION_REVIEW_STATUS_FIELD) return 'Datos correctos';
  const fieldMatch = /^col_(\d+)$/.exec(String(field || ''));

  if (!fieldMatch) {
    return String(field || 'Filtro');
  }

  const columnIndex = Number(fieldMatch[1]);
  return getColumnDisplayName(headers[columnIndex], sheetName) || `Columna ${columnIndex}`;
}

function formatGridFilterChipLabel(filterItem, headers, sheetName) {
  const headerLabel = getGridHeaderLabel(filterItem.field, headers, sheetName);
  if (filterItem.field === LOCATION_REVIEW_STATUS_FIELD) {
    return `${headerLabel}: ${getLocationReviewFilterLabel(filterItem.value)}`;
  }
  const operatorLabel = GRID_STRING_OPERATOR_LABELS[filterItem.operator] || filterItem.operator;

  if (GRID_FILTER_OPERATORS_WITHOUT_VALUE.has(filterItem.operator)) {
    return `${headerLabel}: ${operatorLabel}`;
  }

  if (Array.isArray(filterItem.value)) {
    return `${headerLabel}: ${operatorLabel} ${filterItem.value.join(', ')}`;
  }

  return `${headerLabel}: ${operatorLabel} ${String(filterItem.value || '')}`;
}

function normalizeHeaderName(value) {
  return String(value || '').trim().toLowerCase();
}

function formatRowDisplayValue(header, value, headers, row, fallback = '-') {
  return isPhoneColumnLabel(header)
    ? formatPhoneValueForRow(header, value, headers, row, fallback)
    : formatDateValueForLabel(header, value, fallback);
}

function isHiddenUsuariosUiHeader(header) {
  return HIDDEN_USUARIOS_UI_HEADER_NAMES.has(normalizeHeaderName(header));
}

function getHiddenUsuariosUiDefaultValue(header) {
  return HIDDEN_USUARIOS_UI_DEFAULT_VALUES[normalizeHeaderName(header)] || '';
}

function formatRequestDetailLabel(header, {
  stripRequesterPrefix = false,
  stripTargetPrefix = false,
  stripCurrentSuffix = false,
} = {}) {
  let nextLabel = String(header || '').trim();

  if (stripRequesterPrefix) {
    nextLabel = nextLabel.replace(/^Solicitado por\s*-\s*/i, '');
  }

  if (stripTargetPrefix) {
    nextLabel = nextLabel.replace(/^Usuario\s*-\s*/i, '');
  }

  if (stripCurrentSuffix) {
    nextLabel = nextLabel.replace(/\s+actual$/i, '');
  }

  return nextLabel || 'Dato';
}

function GridFilterApplyButton({ disabled, onApplyFilter }) {
  return (
    <Tooltip title="Aplicar filtro" arrow>
      <span>
        <Button
          type="button"
          size="small"
          variant="contained"
          disabled={disabled}
          onClick={onApplyFilter}
          aria-label="Aplicar filtro"
          sx={{
            ml: 0.75,
            minWidth: 84,
            px: 1.25,
            py: 0.5,
            flexShrink: 0,
            textTransform: 'none',
            borderRadius: 1.25,
            fontSize: 12,
            lineHeight: 1.2,
          }}
        >
          Buscar
        </Button>
      </span>
    </Tooltip>
  );
}

function GridFilterTextInput(props) {
  const {
    item,
    applyValue,
    focusElementRef,
    isApplyDisabled = true,
    onApplyFilter = () => {},
  } = props;
  const [inputValue, setInputValue] = useState(String(item.value || ''));
  const hasLocalValueChanges = String(inputValue) !== String(item.value || '');
  const disableApplyButton = isApplyDisabled && !hasLocalValueChanges;

  useEffect(() => {
    setInputValue(String(item.value || ''));
  }, [item.value]);

  const applyDraftFilterValue = useCallback(() => {
    const nextItem = {
      ...item,
      value: String(inputValue || ''),
    };

    applyValue(nextItem);
    onApplyFilter(nextItem);
  }, [applyValue, inputValue, item, onApplyFilter]);

  return (
    <TextField
      inputRef={focusElementRef}
      variant="standard"
      placeholder="Escribe o selecciona"
      value={inputValue}
      onChange={(event) => {
        setInputValue(String(event.target.value || ''));
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          applyDraftFilterValue();
        }
      }}
      size="small"
      fullWidth
      sx={{ minWidth: 0 }}
      InputProps={{
        endAdornment: (
          <GridFilterApplyButton disabled={disableApplyButton} onApplyFilter={applyDraftFilterValue} />
        ),
      }}
    />
  );
}

function UsuariosFilterAutocompleteInput(props) {
  const {
    item,
    applyValue,
    focusElementRef,
    valueOptions = [],
    isPhoneColumn = false,
    isApplyDisabled = true,
    onApplyFilter = () => {},
  } = props;
  const [inputValue, setInputValue] = useState(String(item.value || ''));
  const hasLocalValueChanges = String(inputValue) !== String(item.value || '');
  const disableApplyButton = isApplyDisabled && !hasLocalValueChanges;

  useEffect(() => {
    setInputValue(String(item.value || ''));
  }, [item.value]);

  const applyDraftFilterValue = useCallback(() => {
    const nextItem = {
      ...item,
      value: String(inputValue || ''),
    };

    applyValue(nextItem);
    onApplyFilter(nextItem);
  }, [applyValue, inputValue, item, onApplyFilter]);

  return (
    <Autocomplete
      freeSolo
      options={valueOptions}
      value={inputValue}
      inputValue={inputValue}
      filterOptions={isPhoneColumn ? phoneFilterAutocomplete : usuariosFilterAutocomplete}
      openOnFocus
      autoHighlight
      forcePopupIcon
      onChange={(_, newValue) => {
        setInputValue(typeof newValue === 'string' ? newValue : '');
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(String(newInputValue || ''));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={focusElementRef}
          variant="standard"
          placeholder="Escribe o selecciona"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyDraftFilterValue();
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <GridFilterApplyButton disabled={disableApplyButton} onApplyFilter={applyDraftFilterValue} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      size="small"
      fullWidth
      sx={{ minWidth: 0 }}
      clearOnBlur={false}
      handleHomeEndKeys
      selectOnFocus
    />
  );
}

function CounterBubble({ value }) {
  return (
    <Box
      component="span"
      sx={{
        minWidth: 22,
        height: 22,
        px: 0.5,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {value}
    </Box>
  );
}

const SheetsGrid = memo(function SheetsGrid({
  hasData,
  gridRows,
  gridColumns,
  loading,
  gridInitialState,
  paginationModel,
  onPaginationModelChange,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  isMobileViewport,
  useAutoRowHeight = false,
}) {
  if (!hasData) {
    return (
      <Typography sx={{ color: 'text.secondary' }}>
        No hay datos cargados.
      </Typography>
    );
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', height: { xs: '62vh', md: '70vh' }, pt: 1.5 }}>
      <DataGrid
        rows={gridRows}
        columns={gridColumns}
        loading={loading}
        pageSizeOptions={isMobileViewport ? [5, 10, 25, 100] : [10, 25, 50, 100]}
        initialState={gridInitialState}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        getRowHeight={useAutoRowHeight ? () => 'auto' : undefined}
        getEstimatedRowHeight={useAutoRowHeight ? () => 108 : undefined}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        ignoreDiacritics
        disableRowSelectionOnClick
        density={isMobileViewport ? 'compact' : 'standard'}
        sx={{
          width: '100%',
          minWidth: 0,
          '& .MuiDataGrid-menuIconButton': {
            opacity: 1,
            visibility: 'visible',
          },
          '& .MuiDataGrid-menuIcon': {
            visibility: 'visible',
            width: 'auto',
          },
          '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
            opacity: 1,
            pointerEvents: 'auto',
          },
          '& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller': {
            scrollbarGutter: 'stable',
          },
          '& .MuiDataGrid-scrollbar--horizontal': {
            backgroundColor: '#dfe7fb',
            borderTop: '1px solid rgba(47, 102, 243, 0.18)',
            scrollbarColor: '#2f66f3 #dfe7fb',
          },
          '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar': {
            height: 16,
          },
          '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-track': {
            backgroundColor: '#dfe7fb',
          },
          '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb': {
            backgroundColor: '#2f66f3',
            borderRadius: 999,
            border: '2px solid #dfe7fb',
          },
          '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#1f54db',
          },
        }}
        slotProps={{
          panel: {
            sx: {
              width: 'min(92vw, 1100px)',
              maxWidth: 'calc(100vw - 32px)',
              '&.MuiDataGrid-panel': {
                width: 'min(92vw, 1100px)',
                maxWidth: 'calc(100vw - 32px)',
              },
              '& .MuiDataGrid-paper, & .MuiDataGrid-panelWrapper, & .MuiDataGrid-panelContent': {
                width: '100%',
                maxWidth: '100%',
              },
            },
          },
          filterPanel: {
            sx: {
              width: '100%',
              '& .MuiDataGrid-filterForm': {
                width: '100%',
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 12,
                margin: 0,
                padding: 0,
              },
              '& .MuiDataGrid-filterForm > *': {
                margin: 0,
              },
              '& .MuiDataGrid-filterForm > .MuiFormControl-root:first-of-type': {
                display: 'none',
              },
              '& .MuiDataGrid-filterFormLogicOperatorInput': {
                display: 'none',
                flex: '0 0 0',
                width: 0,
                minWidth: 0,
                margin: 0,
                padding: 0,
              },
              '& .MuiDataGrid-filterFormDeleteIcon': {
                flex: '0 0 32px',
                width: 32,
                minWidth: 32,
                display: 'flex',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-filterFormColumnInput': {
                flex: '0 0 22%',
                minWidth: 170,
                maxWidth: 260,
              },
              '& .MuiDataGrid-filterFormOperatorInput': {
                flex: '0 0 20%',
                minWidth: 150,
                maxWidth: 240,
              },
              '& .MuiDataGrid-filterFormValueInput': {
                flex: '1 1 auto',
                minWidth: 0,
                width: '100%',
                maxWidth: 'none',
              },
              '& .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput': {
                width: '100%',
              },
              '& .MuiDataGrid-filterFormValueInput .MuiFormControl-root, & .MuiDataGrid-filterFormValueInput .MuiAutocomplete-root': {
                width: '100%',
              },
              '@media (max-width: 900px)': {
                '& .MuiDataGrid-filterForm': {
                  flexWrap: 'wrap',
                },
                '& .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput': {
                  flex: '1 1 220px',
                  maxWidth: 'none',
                },
                '& .MuiDataGrid-filterFormValueInput': {
                  flexBasis: '100%',
                },
              },
            },
          },
        }}
      />
    </Box>
  );
});

function AdminDashboard({
  accessToken,
  locationRequests = EMPTY_LOCATION_REQUESTS,
  onLocationRequestUpsert = () => {},
  onCancelRequest,
  cancellingRequests = false,
}) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const authClaims = useMemo(() => decodeJwtPayload(accessToken) || {}, [accessToken]);
  const isMainAdminUser = authClaims?.level === 1 && authClaims?.role === 'main_admin';
  const isReadOnlyUser = authClaims?.level === 3 && authClaims?.role === 'regular_user';
  const [editStatusSaving, setEditStatusSaving] = useState(false);
  const [editStatusDrafts, setEditStatusDrafts] = useState({});
  const [editStatusFeedback, setEditStatusFeedback] = useState(null);
  const adminDropdownSx = useMemo(() => (isMainAdminUser
    ? {
      '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
        backgroundColor: 'background.paper',
        borderRadius: 1.5,
        boxShadow: '0 0 0 1px rgba(31, 42, 68, 0.08)',
      },
      '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(31, 42, 68, 0.28)',
        borderWidth: 1.5,
      },
      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
      '&.MuiOutlinedInput-root.Mui-focused, & .MuiOutlinedInput-root.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(47, 102, 243, 0.12)',
      },
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: 2,
      },
    }
    : undefined), [isMainAdminUser]);
  const adminDropdownMenuProps = useMemo(() => (isMainAdminUser
    ? {
      MenuListProps: {
        sx: {
          py: 0.5,
        },
      },
      PaperProps: {
        elevation: 0,
        sx: {
          mt: 0.5,
          border: '1.5px solid',
          borderColor: 'rgba(31, 42, 68, 0.18)',
          borderRadius: 1.75,
          boxShadow: '0 14px 32px rgba(31, 42, 68, 0.14)',
          maxHeight: 'min(460px, calc(100dvh - 140px))',
          overflowY: 'auto',
          overflowX: 'hidden',
        },
      },
    }
    : undefined), [isMainAdminUser]);

  const {
    sheets,
    selectedSheet,
    headers,
    rows,
    createSheetName,
    newSheetName,
    selectedColumnName,
    newColumnName,
    addColumnName,
    editingRowIndex,
    loading: sheetsLoading,
    refreshing,
    hasUnsavedDraft,
    error,
    canCreateSheet,
    canRenameSheet,
    canRenameColumn,
    canAddColumn,
    rowInputs,
    setRowDraft,
    setSelectedSheet,
    setCreateSheetName,
    setNewSheetName,
    setSelectedColumnName,
    setNewColumnName,
    setAddColumnName,
    loadSheetData,
    handleCreateSheet,
    handleDeleteSheet,
    handleRenameSheet,
    handleRenameColumn,
    handleAddColumn,
    handleDeleteColumn,
    submitRow,
    handleDeleteRow,
    startEditRow,
    cancelEditRow,
    updateRowDraft,
  } = useSheetsDashboard(accessToken, {
    preferredInitialSheetName: 'Usuarios',
  });
  const loading = sheetsLoading || editStatusSaving;
  const editSaveContext = useRef({ accessToken, selectedSheet, mounted: true, generation: 0 });
  if (editSaveContext.current.accessToken !== accessToken || editSaveContext.current.selectedSheet !== selectedSheet) {
    editSaveContext.current = { accessToken, selectedSheet, mounted: true, generation: editSaveContext.current.generation + 1 };
  }
  useEffect(() => {
    editSaveContext.current.mounted = true;
    return () => {
      editSaveContext.current.mounted = false;
      editSaveContext.current.generation += 1;
    };
  }, []);

  const locationStatusState = useLocationStatuses(accessToken, {
    enabled: isReadOnlyUser || isMainAdminUser,
    rows,
    requests: locationRequests,
  });
  const userLocationStatusIndex = useMemo(() => createUserLocationStatusIndex(locationStatusState.userStatuses), [locationStatusState.userStatuses]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  useEffect(() => {
    setEditStatusSaving(false);
    setEditStatusDrafts({});
    setEditStatusFeedback(null);
    setEditDialogOpen(false);
    setDuplicateUserDialog({ open: false, conflict: null, submitting: false });
  }, [accessToken, selectedSheet]);
  const [duplicateUserDialog, setDuplicateUserDialog] = useState({
    open: false,
    conflict: null,
    submitting: false,
  });
  const [createSheetDialogOpen, setCreateSheetDialogOpen] = useState(false);
  const [renameSheetDialogOpen, setRenameSheetDialogOpen] = useState(false);
  const [addColumnDialogOpen, setAddColumnDialogOpen] = useState(false);
  const [renameColumnDialogOpen, setRenameColumnDialogOpen] = useState(false);
  const [deleteSheetDialogOpen, setDeleteSheetDialogOpen] = useState(false);
  const [deleteColumnDialogOpen, setDeleteColumnDialogOpen] = useState(false);
  const addColumnInputRef = useRef(null);
  const [deleteRowDialog, setDeleteRowDialog] = useState({
    open: false,
    rowIndex: null,
    note: '',
    roleReplacementName: '',
  });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    email: '',
    locationLabel: '',
  });
  const [passwordDialogBusy, setPasswordDialogBusy] = useState(false);
  useEffect(() => {
    setPasswordDialogBusy(false);
    setPasswordDialog({ open: false, email: '', locationLabel: '' });
  }, [accessToken, selectedSheet]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [passwordStatusByEmail, setPasswordStatusByEmail] = useState({});
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  const [requestDialog, setRequestDialog] = useState({
    open: false,
    rowIndex: null,
    locationIndex: null,
  });
  const [expandedRequestSection, setExpandedRequestSection] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestFeedback, setRequestFeedback] = useState({
    type: '',
    message: '',
  });
  const [overrideDialog, setOverrideDialog] = useState({
    open: false,
    message: '',
    action: '',
    payload: null,
  });
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [newUserRequestInputs, setNewUserRequestInputs] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    laborRol: '',
    idioma: '',
  });
  const [rowRequestInputs, setRowRequestInputs] = useState({
    requestedRole: '',
    newState: '',
    newCity: '',
    newChurchName: '',
    newCountry: '',
    requestedNombre: '',
    requestedApellido: '',
    requestedTelefono: '',
    requestedEmail: '',
    requestedIdioma: '',
    deleteNote: '',
  });
  const [locationSuggestions, setLocationSuggestions] = useState({
    states: [],
    cities: [],
    churchNames: [],
    countries: [],
    languages: [],
  });
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isNewUserRequestOpen, setIsNewUserRequestOpen] = useState(false);
  const [createPasswordAfterAdd, setCreatePasswordAfterAdd] = useState(false);
  const [requestPendingCounts, setRequestPendingCounts] = useState({});
  const [pendingSheetsDialogOpen, setPendingSheetsDialogOpen] = useState(false);
  const [adminPendingRequests, setAdminPendingRequests] = useState([]);
  const [pendingRequestsDialog, setPendingRequestsDialog] = useState({
    open: false,
    email: '',
  });
  const [requestRowAction, setRequestRowAction] = useState({
    mode: '',
    rowIndex: null,
  });
  const [requesterInfoDialog, setRequesterInfoDialog] = useState({
    open: false,
    rowIndex: null,
  });
  const [applyNewUserDialog, setApplyNewUserDialog] = useState({
    open: false,
    rowIndex: null,
    createdValue: '',
  });
  const [gridFilterModel, setGridFilterModel] = useState(EMPTY_GRID_FILTER_MODEL);
  const [globalSearch, setGlobalSearch] = useState('');
  const [gridPaginationModel, setGridPaginationModel] = useState({ pageSize: 100, page: 0 });
  const [activeGridFilterItems, setActiveGridFilterItems] = useState([]);
  const [gridColumnVisibilityModel, setGridColumnVisibilityModel] = useState({});
  const [hideLocationColumnsQuick, setHideLocationColumnsQuick] = useState(false);
  const lastInteractedGridFilterFieldRef = useRef('');
  const [activeLocationTab, setActiveLocationTab] = useState('all');
  const [locationDraftCount, setLocationDraftCount] = useState(1);
  const [locationDetailsDialog, setLocationDetailsDialog] = useState({
    open: false,
    rowIndex: null,
  });

  useEffect(() => {
    if (!addColumnDialogOpen) {
      return undefined;
    }

    const focusTimer = setTimeout(() => {
      addColumnInputRef.current?.focus();
    }, 50);

    return () => clearTimeout(focusTimer);
  }, [addColumnDialogOpen]);

  useEffect(() => {
    if (!accessToken) {
      setRoleOptions([]);
      setRolesError('');
      setRolesLoading(false);
      return;
    }

    let cancelled = false;

    async function loadRoleOptions() {
      setRolesLoading(true);
      setRolesError('');

      try {
        let nextRoleOptions = [];

        if (isMainAdminUser) {
          const data = await createSheetCache(accessToken).fetchSheet('Roles');
          const headersFromSheet = data.headers || [];
          const rowsFromSheet = data.rows || [];
          const roleNameIndex = headersFromSheet.findIndex(
            (header) => String(header || '').trim().toLowerCase() === 'nombre'
          );
          const sourceIndex = roleNameIndex >= 0 ? roleNameIndex : 0;

          nextRoleOptions = Array.from(
            new Set(
              rowsFromSheet
                .map((row) => String(row?.[sourceIndex] || '').trim())
                .filter(Boolean)
            )
          );
        } else {
          const optionsResult = await fetchRequestRoleOptions(accessToken);
          nextRoleOptions = Array.from(
            new Set((optionsResult?.roles || []).map((role) => String(role || '').trim()).filter(Boolean))
          );
        }

        if (!cancelled) {
          setRoleOptions(nextRoleOptions);
        }
      } catch {
        if (!cancelled) {
          setRoleOptions([]);
          setRolesError('No se pudieron cargar los roles desde la hoja Roles.');
        }
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    }

    loadRoleOptions();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isMainAdminUser]);

  useEffect(() => {
    if (!accessToken) {
      setLocationSuggestions({ states: [], cities: [], churchNames: [], countries: [], languages: [] });
      return;
    }

    let cancelled = false;

    async function loadLocationSuggestions() {
      try {
        const data = await fetchUsuariosLocationSuggestions(accessToken);
        const states = Array.isArray(data?.states) ? data.states : [];
        const cities = Array.isArray(data?.cities) ? data.cities : [];
        const churchNames = Array.isArray(data?.churchNames) ? data.churchNames : [];
        const countries = Array.isArray(data?.countries) ? data.countries : [];
        const languages = Array.isArray(data?.languages) ? data.languages : [];

        if (!cancelled) {
          setLocationSuggestions({ states, cities, churchNames, countries, languages });
        }
      } catch {
        if (!cancelled) {
          setLocationSuggestions({ states: [], cities: [], churchNames: [], countries: [], languages: [] });
        }
      }
    }

    loadLocationSuggestions();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    setErrorToastOpen(Boolean(error));
  }, [error]);

  const isRequestSheetName = useCallback((sheetName) => {
    const normalized = String(sheetName || '').trim().toLowerCase();
    return REQUEST_SHEET_NAMES.some(
      (requestSheetName) => String(requestSheetName || '').trim().toLowerCase() === normalized
    );
  }, []);

  useEffect(() => {
    if (!accessToken || !isMainAdminUser) {
      setRequestPendingCounts({});
      return;
    }

    const requestSheets = sheets
      .map((sheet) => String(sheet?.title || '').trim())
      .filter((title) => isRequestSheetName(title));

    if (requestSheets.length === 0) {
      setRequestPendingCounts({});
      return;
    }

    let cancelled = false;

    async function loadRequestPendingCounts() {
      try {
        const data = await fetchRequestPendingCounts(accessToken);

        if (!cancelled) {
          setRequestPendingCounts(data?.counts || {});
        }
      } catch {
        if (!cancelled) {
          setRequestPendingCounts({});
        }
      }
    }

    loadRequestPendingCounts();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isMainAdminUser, isRequestSheetName, sheets]);

  useEffect(() => {
    if (!accessToken || !isMainAdminUser) {
      setAdminPendingRequests([]);
      return;
    }

    let cancelled = false;

    async function loadAdminPendingRequests() {
      try {
        const result = await fetchPendingUserRequests(accessToken);

        if (!cancelled) {
          setAdminPendingRequests(Array.isArray(result?.requests) ? result.requests : []);
        }
      } catch {
        if (!cancelled) {
          setAdminPendingRequests([]);
        }
      }
    }

    loadAdminPendingRequests();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isMainAdminUser]);

  useEffect(() => {
    if (!isMainAdminUser || !isRequestSheetName(selectedSheet) || loading) {
      return;
    }

    const hasRequestSheetSignature = headers.some((header) => {
      const normalizedHeader = normalizeHeaderName(header);

      return (
        normalizedHeader === normalizeHeaderName(REQUEST_SUBMITTED_AT_HEADER)
        || normalizedHeader.startsWith(REQUEST_REQUESTER_HEADER_PREFIX)
        || normalizedHeader.startsWith(REQUEST_TARGET_HEADER_PREFIX)
      );
    });

    if (!hasRequestSheetSignature) {
      return;
    }

    setRequestPendingCounts((previous) => ({
      ...previous,
      [selectedSheet]: rows.length,
    }));
  }, [headers, isMainAdminUser, isRequestSheetName, loading, rows.length, selectedSheet]);

  const totalPendingRequests = useMemo(
    () => Object.values(requestPendingCounts).reduce((total, count) => total + Number(count || 0), 0),
    [requestPendingCounts]
  );

  const pendingRequestSheetItems = useMemo(
    () => REQUEST_SHEET_NAMES.map((sheetName) => ({
      name: sheetName,
      pendingCount: Number(requestPendingCounts[sheetName] || 0),
      available: sheets.some((sheet) => String(sheet?.title || '').trim() === sheetName),
    })),
    [requestPendingCounts, sheets]
  );

  const visiblePendingRequests = useMemo(() => {
    if (isMainAdminUser) {
      return adminPendingRequests;
    }

    if (isReadOnlyUser) {
      return locationRequests;
    }

    return [];
  }, [adminPendingRequests, isMainAdminUser, isReadOnlyUser, locationRequests]);

  const pendingRequestsByEmail = useMemo(
    () => visiblePendingRequests.reduce((accumulator, request) => {
      const normalizedEmail = String(request?.usuarioEmail || '').trim().toLowerCase();

      if (!normalizedEmail) {
        return accumulator;
      }

      if (!accumulator[normalizedEmail]) {
        accumulator[normalizedEmail] = [];
      }

      accumulator[normalizedEmail].push(request);
      return accumulator;
    }, {}),
    [visiblePendingRequests]
  );

  const activePendingRequests = useMemo(
    () => pendingRequestsByEmail[pendingRequestsDialog.email] || [],
    [pendingRequestsByEmail, pendingRequestsDialog.email]
  );

  const isSelectedRequestSheet = useMemo(
    () => isRequestSheetName(selectedSheet),
    [isRequestSheetName, selectedSheet]
  );

  const createPanelTitle = useMemo(() => {
    if (!selectedSheet) {
      return 'Formulario de creación';
    }

    return `Formulario de ${getSheetDisplayName(selectedSheet)}`;
  }, [selectedSheet]);

  const renderSheetOptionLabel = useCallback((sheetTitle) => {
    if (!isMainAdminUser || !isRequestSheetName(sheetTitle)) {
      return getSheetDisplayName(sheetTitle);
    }

    const pendingCount = Number(requestPendingCounts[sheetTitle] || 0);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>{sheetTitle}</span>
        <CounterBubble value={pendingCount} />
      </Box>
    );
  }, [isMainAdminUser, isRequestSheetName, requestPendingCounts]);

  async function submitEditDialog(event) {
    event.preventDefault();
    await saveEditDialog();
  }

  async function saveEditDialog(duplicateResolution = '') {
    if (editStatusSaving) return;
    const generation = editSaveContext.current.generation;
    const isCurrentSave = () => editSaveContext.current.mounted
      && editSaveContext.current.generation === generation
      && editSaveContext.current.accessToken === accessToken
      && editSaveContext.current.selectedSheet === selectedSheet;
    const changes = isUsuariosSheet ? getSelectedLocationStatusChanges(rowInputLocations, editStatusDrafts) : [];
    let savedSnapshot;
    let userWasSaved = false;
    setEditStatusSaving(true);
    setEditStatusFeedback(null);
    try {
      const outcome = await saveUserWithLocationStatuses({
        changes,
        saveUser: async () => {
          const success = await submitRow(rowInputsForSubmit, {
            duplicateResolution,
            keepEditing: changes.length > 0,
            onSaved: (snapshot) => { savedSnapshot = snapshot; },
            onError: (requestError) => {
              if (requestError?.data?.code === 'DUPLICATE_USER_EMAIL') {
                setDuplicateUserDialog({ open: true, conflict: requestError.data, submitting: false });
                return true;
              }
              return false;
            },
          });
          userWasSaved = success;
          return {
            success,
            locations: savedSnapshot?.row ? getIndexedLocationsFromRow(savedSnapshot.headers, savedSnapshot.row) : [],
          };
        },
        updateStatus: (scope, status) => {
          if (!isCurrentSave()) throw new Error('La sesión o la hoja cambió.');
          const savedEmailIndex = (savedSnapshot?.headers || []).findIndex((header) => String(header).trim().toLowerCase() === 'email');
          const savedEmail = savedEmailIndex >= 0 ? savedSnapshot.row[savedEmailIndex] : '';
          if (!savedEmail) throw new Error('No se pudo verificar el email del usuario guardado.');
          return updateUserLocationStatus(savedEmail, scope, status, accessToken);
        },
      });
      if (!isCurrentSave() || !outcome.userSaved) return;
      setDuplicateUserDialog({ open: false, conflict: null, submitting: false });
      setEditStatusDrafts((previous) => {
        const next = { ...previous };
        outcome.saved.forEach(({ change }) => { delete next[locationStatusKey(change.locationScope)]; });
        return next;
      });
      await locationStatusState.refresh();
      if (!isCurrentSave()) return;
      const warnings = outcome.saved.filter(({ result }) => result?.notification?.status === 'failed')
        .map(({ result }) => result.notification.message);
      if (outcome.failed.length) {
        setEditStatusFeedback({
          type: 'warning',
          message: [`Los datos del usuario se guardaron. No se pudo guardar su revisión en ${outcome.failed.length} sede(s): ${outcome.failed.map(({ error: statusError }) => statusError.message).join(' ')} Puedes volver a guardar para reintentar.`, ...warnings].join(' '),
        });
        return;
      }
      if (outcome.skipped.length) warnings.push('No se modificó la revisión del usuario en las sedes que no se pudieron verificar. Actualiza la tabla antes de volver a editarlas.');
      if (changes.length) {
        setRequestFeedback({
          type: warnings.length ? 'warning' : 'success',
          message: ['Los datos del usuario se guardaron.', outcome.saved.length ? `Se actualizó su revisión en ${outcome.saved.length} sede(s).` : '', ...warnings].filter(Boolean).join(' '),
        });
      }
      setEditDialogOpen(false);
      setEditStatusDrafts({});
      cancelEditRow();
    } catch (saveError) {
      if (!isCurrentSave()) return;
      const feedback = {
        type: userWasSaved ? 'warning' : 'error',
        message: `${userWasSaved ? 'Los datos del usuario se guardaron. ' : ''}No se pudieron completar los cambios. ${saveError.message || 'Actualiza la tabla antes de volver a intentarlo.'}`,
      };
      if (userWasSaved && !savedSnapshot?.row) {
        setRequestFeedback(feedback);
        setEditDialogOpen(false);
        cancelEditRow();
      } else {
        setEditStatusFeedback(feedback);
      }
    } finally {
      if (isCurrentSave()) setEditStatusSaving(false);
    }
  }

  async function resolveDuplicateUser(duplicateResolution) {
    const generation = editSaveContext.current.generation;
    setDuplicateUserDialog((previous) => ({ ...previous, submitting: true }));
    await saveEditDialog(duplicateResolution);
    if (editSaveContext.current.mounted && editSaveContext.current.generation === generation) {
      setDuplicateUserDialog((previous) => ({ ...previous, submitting: false }));
    }
  }

  function renderDuplicateUserCard({ row, rowIndex, kind }) {
    const values = Array.isArray(row) ? row : [];
    const valueFor = (columnName) => {
      const columnIndex = headers.findIndex(
        (header) => normalizeHeaderName(header) === normalizeHeaderName(columnName)
      );
      return columnIndex >= 0 ? String(values[columnIndex] || '').trim() : '';
    };
    const fullName = [valueFor('Nombre'), valueFor('Apellido')].filter(Boolean).join(' ')
      || 'Usuario sin nombre';
    const locations = getIndexedLocationsFromRow(headers, values);
    const isEdited = kind === 'edited';

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          borderColor: isEdited ? 'primary.main' : 'divider',
          bgcolor: isEdited ? 'action.selected' : 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{fullName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Fila #{Number(rowIndex) + 1}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={isEdited ? 'primary' : 'default'}
            label={isEdited ? 'Con tus cambios' : 'Ya guardada'}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.6 }}>
          {[
            ['Email', valueFor('Email')],
            ['Teléfono', formatPhoneValueForRow('Telefono', valueFor('Telefono'), headers, values)],
            ['Rol', valueFor('Labor/Rol')],
            ['Idioma', valueFor('Idioma')],
          ].map(([label, value]) => (
            <Fragment key={label}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
            </Fragment>
          ))}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Sedes</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={`Sedes de ${fullName}`}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, px: 1, py: 0.75 }}>País</TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 1, py: 0.75 }}>Estado/Prov./Dept.</TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 1, py: 0.75 }}>Ciudad</TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 1, py: 0.75 }}>Barrio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length > 0 ? locations.map((location, index) => (
                  <TableRow key={`${rowIndex}-${index}`}>
                    <TableCell sx={{ px: 1, py: 0.75 }}>{location.country || '—'}</TableCell>
                    <TableCell sx={{ px: 1, py: 0.75 }}>{location.state || '—'}</TableCell>
                    <TableCell sx={{ px: 1, py: 0.75 }}>{location.city || '—'}</TableCell>
                    <TableCell sx={{ px: 1, py: 0.75 }}>{location.churchName || '—'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary', px: 1, py: 1 }}>
                      Sin sedes asignadas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Cada fila representa una sede. Si aparece “/” dentro de una celda, ese carácter está guardado directamente en la hoja.
          </Typography>
        </Box>

        <Button
          variant={isEdited ? 'contained' : 'outlined'}
          onClick={() => resolveDuplicateUser(isEdited ? 'keepEdited' : 'keepExisting')}
          disabled={duplicateUserDialog.submitting}
          fullWidth
          sx={{ mt: 'auto' }}
        >
          Conservar este usuario
        </Button>
      </Paper>
    );
  }

  const openEditDialog = useCallback((rowIndex) => {
    setEditStatusDrafts({});
    setEditStatusFeedback(null);
    startEditRow(rowIndex);
    setEditDialogOpen(true);
  }, [startEditRow]);

  const closeEditDialog = useCallback(() => {
    if (loading) return;
    setEditDialogOpen(false);
    setEditStatusDrafts({});
    setEditStatusFeedback(null);
    cancelEditRow();
  }, [cancelEditRow, loading]);

  const openDeleteRowDialog = useCallback((rowIndex) => {
    setDeleteRowDialog({ open: true, rowIndex, note: '', roleReplacementName: '' });
  }, []);

  const getLocationLabelByRowIndex = useCallback((rowIndex) => {
    const indexes = getLocationColumnIndexes(headers);
    const row = rows[rowIndex] || [];
    const city = indexes.cityIndex >= 0
      ? String(row[indexes.cityIndex] || '').trim()
      : '';
    const state = indexes.stateIndex >= 0
      ? String(row[indexes.stateIndex] || '').trim()
      : '';

    if (city && state) {
      return `${city}, ${state}`;
    }

    return city || state || '';
  }, [headers, rows]);

  const openPasswordDialog = useCallback((email, locationLabel = '') => {
    setPasswordDialogBusy(false);
    setPasswordDialog({
      open: true,
      email: String(email || '').trim(),
      locationLabel: String(locationLabel || '').trim(),
    });
  }, []);

  const closePasswordDialog = useCallback(() => {
    if (passwordDialogBusy) return;
    setPasswordDialog({
      open: false,
      email: '',
      locationLabel: '',
    });
  }, [passwordDialogBusy]);

  const refreshPasswordLocationStatuses = locationStatusState.refresh;
  const handlePasswordCompleted = useCallback(async ({ email } = {}) => {
    const generation = editSaveContext.current.generation;
    const isCurrent = () => editSaveContext.current.mounted
      && editSaveContext.current.accessToken === accessToken
      && editSaveContext.current.generation === generation;
    if (!isCurrent()) return;
    createSheetCache(accessToken).invalidateAll();
    const results = await Promise.allSettled([
      refreshPasswordLocationStatuses(),
      email ? fetchUserPasswordStatuses(accessToken, [email]) : Promise.resolve(null),
    ]);
    if (!isCurrent()) return;
    if (results[1].status === 'fulfilled' && results[1].value?.statuses) {
      setPasswordStatusByEmail((previous) => ({ ...previous, ...results[1].value.statuses }));
    }
  }, [accessToken, refreshPasswordLocationStatuses]);

  const handleCloseErrorToast = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorToastOpen(false);
  }, []);

  const handleCloseRequestFeedback = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setRequestFeedback({ type: '', message: '' });
  }, []);

  const handleCloseRolesErrorToast = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setRolesError('');
  }, []);

  const toFriendlyRequestMessage = useCallback((rawMessage, fallbackMessage) => {
    const normalized = String(rawMessage || '').trim();

    if (!normalized) {
      return fallbackMessage;
    }

    const firstLine = normalized.split('\n')[0];
    const withoutPrefix = firstLine
      .replace(/^Unexpected response type:[^-]*-\s*/i, '')
      .replace(/^HttpError:\s*/i, '')
      .trim();

    const cleanMessage = withoutPrefix.split(' at ')[0].trim();

    if (/^Campos obligatorios faltantes:/i.test(cleanMessage)) {
      return cleanMessage;
    }

    if (
      /Unexpected response type|Request failed|Error inesperado|Received invalid JSON|API returned HTML/i.test(
        normalized
      )
    ) {
      return fallbackMessage;
    }

    return cleanMessage || fallbackMessage;
  }, []);

  const isDuplicateRequestError = useCallback((rawMessage) => {
    const normalized = String(rawMessage || '').toLowerCase();
    return normalized.includes('ya existe una solicitud');
  }, []);

  const isDuplicateExistingUserError = useCallback((rawMessage) => {
    const normalized = String(rawMessage || '').toLowerCase();
    return normalized.includes('ya está registrado') || normalized.includes('ya esta registrado') || normalized.includes('no se puede duplicar');
  }, []);

  const openOverrideDialog = useCallback((action, payload, rawMessage) => {
    const promptText = toFriendlyRequestMessage(
      rawMessage,
      'Ya existe una solicitud previa. ¿Deseas sobrescribirla?'
    );

    setOverrideDialog({
      open: true,
      message: promptText,
      action,
      payload,
    });
  }, [toFriendlyRequestMessage]);

  const closeOverrideDialog = useCallback(() => {
    if (overrideSubmitting) {
      return;
    }

    setOverrideDialog({
      open: false,
      message: '',
      action: '',
      payload: null,
    });
  }, [overrideSubmitting]);

  const confirmOverrideDialog = useCallback(async () => {
    if (!overrideDialog.action || !overrideDialog.payload) {
      return;
    }

    setOverrideSubmitting(true);
    setRequestSubmitting(true);

    try {
      let result;

      if (overrideDialog.action === 'requestNewUser') {
        result = await requestNewUser(
          {
            ...overrideDialog.payload,
            overrideExisting: true,
          },
          accessToken
        );
      } else if (overrideDialog.action === 'requestRoleChange') {
        result = await requestRoleChange(
          {
            ...overrideDialog.payload,
            overrideExisting: true,
          },
          accessToken
        );
      } else if (overrideDialog.action === 'requestMoveUser') {
        result = await requestMoveUser(
          {
            ...overrideDialog.payload,
            overrideExisting: true,
          },
          accessToken
        );
      } else if (overrideDialog.action === 'requestUpdateUserInfo') {
        result = await requestUpdateUserInfo(
          {
            ...overrideDialog.payload,
            overrideExisting: true,
          },
          accessToken
        );
      } else if (overrideDialog.action === 'requestDeleteUser') {
        result = await requestDeleteUser(
          {
            ...overrideDialog.payload,
            overrideExisting: true,
          },
          accessToken
        );
      } else {
        throw new Error('Acción de sobrescritura no soportada.');
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud actualizada correctamente.'));

      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }

      if (overrideDialog.action === 'requestNewUser') {
        setNewUserRequestInputs({
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          laborRol: '',
          idioma: '',
        });
      }

      if (overrideDialog.action === 'requestRoleChange') {
        setRowRequestInputs((previous) => ({ ...previous, requestedRole: '' }));
      }

      if (overrideDialog.action === 'requestMoveUser') {
        setRowRequestInputs((previous) => ({
          ...previous,
          newState: '',
          newCity: '',
          newChurchName: '',
          newCountry: '',
        }));
      }

      if (overrideDialog.action === 'requestUpdateUserInfo') {
        setRowRequestInputs((previous) => ({
          ...previous,
          requestedNombre: String(overrideDialog.payload?.requestedNombre || ''),
          requestedApellido: String(overrideDialog.payload?.requestedApellido || ''),
          requestedTelefono: String(overrideDialog.payload?.requestedTelefono || ''),
          requestedEmail: String(overrideDialog.payload?.requestedEmail || ''),
          requestedIdioma: String(overrideDialog.payload?.requestedIdioma || ''),
        }));
      }

      if (overrideDialog.action === 'requestDeleteUser') {
        setRowRequestInputs((previous) => ({ ...previous, deleteNote: '' }));
      }

      setOverrideDialog({
        open: false,
        message: '',
        action: '',
        payload: null,
      });
    } catch (requestError) {
      setRequestFeedback({
        type: 'error',
        message: toFriendlyRequestMessage(
          requestError.message,
          'No se pudo sobrescribir la solicitud existente.'
        ),
      });
    } finally {
      setOverrideSubmitting(false);
      setRequestSubmitting(false);
    }
  }, [accessToken, onLocationRequestUpsert, overrideDialog, toFriendlyRequestMessage]);

  const clearRequestFeedback = useCallback(() => {
    setRequestFeedback({ type: '', message: '' });
  }, []);

  const openRequestDialog = useCallback((rowIndex, locationIndex = null) => {
    setRequestDialog({ open: true, rowIndex, locationIndex });
    setExpandedRequestSection('');
    clearRequestFeedback();
  }, [clearRequestFeedback, setExpandedRequestSection]);

  const closeRequestDialog = useCallback(() => {
    setRequestDialog({ open: false, rowIndex: null, locationIndex: null });
    setExpandedRequestSection('');
    setRowRequestInputs({
      requestedRole: '',
      newState: '',
      newCity: '',
      newChurchName: '',
      newCountry: '',
      requestedNombre: '',
      requestedApellido: '',
      requestedTelefono: '',
      requestedEmail: '',
      requestedIdioma: '',
      deleteNote: '',
    });
    clearRequestFeedback();
  }, [clearRequestFeedback]);

  const toggleRequestSection = useCallback((sectionKey) => {
    setExpandedRequestSection((previousSection) => (
      previousSection === sectionKey ? '' : sectionKey
    ));
  }, []);

  const openPendingRequestsDialog = useCallback((email) => {
    setPendingRequestsDialog({
      open: true,
      email: String(email || '').trim().toLowerCase(),
    });
  }, []);

  const closePendingRequestsDialog = useCallback(() => {
    setPendingRequestsDialog({
      open: false,
      email: '',
    });
  }, []);

  const openPendingSheetsDialog = useCallback(() => {
    setPendingSheetsDialogOpen(true);
  }, []);

  const closePendingSheetsDialog = useCallback(() => {
    setPendingSheetsDialogOpen(false);
  }, []);

  const openRequestSheetFromPendingDialog = useCallback((sheetName) => {
    if (!sheetName) {
      return;
    }

    setPendingSheetsDialogOpen(false);
    setSelectedSheet(sheetName);
    loadSheetData(sheetName);
  }, [loadSheetData, setSelectedSheet]);

  const openApplyNewUserDialog = useCallback((rowIndex) => {
    setApplyNewUserDialog({
      open: true,
      rowIndex,
      createdValue: '',
    });
  }, []);

  const closeApplyNewUserDialog = useCallback(() => {
    if (requestRowAction.mode) {
      return;
    }

    setApplyNewUserDialog({
      open: false,
      rowIndex: null,
      createdValue: '',
    });
  }, [requestRowAction.mode]);

  const openRequesterInfoDialog = useCallback((rowIndex) => {
    setRequesterInfoDialog({
      open: true,
      rowIndex,
    });
  }, []);

  const closeRequesterInfoDialog = useCallback(() => {
    setRequesterInfoDialog({
      open: false,
      rowIndex: null,
    });
  }, []);

  const removePendingRequestFromCache = useCallback((sheetName, sheetRowNumber) => {
    if (!sheetName || !sheetRowNumber) {
      return;
    }

    const targetSheetRowNumber = Number(sheetRowNumber);

    setAdminPendingRequests((previous) => previous
      .filter(
        (request) => !(
          request.sheetName === sheetName
          && Number(request.sheetRowNumber) === targetSheetRowNumber
        )
      )
      .map((request) => {
        if (request.sheetName !== sheetName || Number(request.sheetRowNumber) < targetSheetRowNumber) {
          return request;
        }

        return {
          ...request,
          sheetRowNumber: Number(request.sheetRowNumber) - 1,
        };
      }));
  }, []);

  async function confirmDeleteSheet() {
    await handleDeleteSheet();
    setDeleteSheetDialogOpen(false);
  }

  async function confirmDeleteColumn() {
    await handleDeleteColumn();
    setDeleteColumnDialogOpen(false);
  }

  async function confirmDeleteRow() {
    if (deleteRowDialog.rowIndex === null) {
      return;
    }

    let targetEmail = '';
    if (isUsuariosSheet && emailColumnIndex >= 0) {
      const targetRow = rows[deleteRowDialog.rowIndex] || [];
      targetEmail = String(targetRow[emailColumnIndex] || '').trim();
    }

    await handleDeleteRow(
      deleteRowDialog.rowIndex,
      targetEmail,
      deleteRowDialog.note,
      deleteRowDialog.roleReplacementName
    );
    setDeleteRowDialog({ open: false, rowIndex: null, note: '', roleReplacementName: '' });
  }

  const runRequestRowAction = useCallback(async (mode, rowIndex, createdValue = '') => {
    if (!selectedSheet) {
      return;
    }

    setRequestRowAction({ mode, rowIndex });
    setRequestFeedback({ type: '', message: '' });

    try {
      if (mode === 'apply') {
        const result = await applyRequestRow(selectedSheet, rowIndex, accessToken, createdValue);
        const notificationSuffix = result?.notification?.sent
          ? ' Se envió una notificación al solicitante.'
          : (result?.notification?.error ? ` No se pudo enviar la notificación: ${result.notification.error}` : '');
        setRequestFeedback({
          type: result?.notification?.error ? 'warning' : 'success',
          message: `${result?.message || 'Solicitud marcada como ejecutada correctamente.'}${notificationSuffix}`,
        });
        if (isMainAdminUser) {
          removePendingRequestFromCache(selectedSheet, Number(rowIndex) + 2);
        }
        await loadSheetData(selectedSheet, { force: true });
        setApplyNewUserDialog({ open: false, rowIndex: null, createdValue: '' });
        return true;
      }

      if (mode === 'dismiss') {
        const success = await handleDeleteRow(rowIndex);
        if (success) {
          if (isMainAdminUser) {
            removePendingRequestFromCache(selectedSheet, Number(rowIndex) + 2);
          }
          setRequestFeedback({
            type: 'success',
            message: 'Solicitud descartada correctamente.',
          });
        }
        return success;
      }
    } catch (error) {
      setRequestFeedback({
        type: 'error',
        message: error.message || 'No se pudo procesar la solicitud.',
      });
      return false;
    } finally {
      setRequestRowAction({ mode: '', rowIndex: null });
    }
    return false;
  }, [accessToken, handleDeleteRow, isMainAdminUser, loadSheetData, removePendingRequestFromCache, selectedSheet]);

  async function executeRequestFromDetails() {
    const rowIndex = requesterInfoDialog.rowIndex;
    if (rowIndex === null) return;

    if (selectedSheet === REQUEST_NEW_USER_SHEET_NAME) {
      closeRequesterInfoDialog();
      openApplyNewUserDialog(rowIndex);
      return;
    }

    const success = await runRequestRowAction('apply', rowIndex);
    if (success) closeRequesterInfoDialog();
  }

  async function dismissRequestFromDetails() {
    const rowIndex = requesterInfoDialog.rowIndex;
    if (rowIndex === null) return;

    const success = await runRequestRowAction('dismiss', rowIndex);
    if (success) closeRequesterInfoDialog();
  }

  const confirmApplyNewUserRequest = useCallback(async () => {
    if (applyNewUserDialog.rowIndex === null) {
      return;
    }

    await runRequestRowAction(
      'apply',
      applyNewUserDialog.rowIndex,
      getHiddenUsuariosUiDefaultValue(CREADO_HEADER)
    );
  }, [applyNewUserDialog.rowIndex, runRequestRowAction]);

  async function submitCreateSheetDialog(event) {
    event.preventDefault();
    const success = await handleCreateSheet(event);
    if (success) {
      setCreateSheetDialogOpen(false);
    }
  }

  async function submitRenameSheetDialog(event) {
    event.preventDefault();
    const success = await handleRenameSheet(event);
    if (success) {
      setRenameSheetDialogOpen(false);
    }
  }

  async function submitAddColumnDialog(event) {
    event.preventDefault();
    const success = await handleAddColumn(event);
    if (success) {
      setAddColumnDialogOpen(false);
    }
  }

  async function submitRenameColumnDialog(event) {
    event.preventDefault();
    const success = await handleRenameColumn(event);
    if (success) {
      setRenameColumnDialogOpen(false);
    }
  }

  const emailColumnIndex = useMemo(
    () =>
      headers.findIndex(
        (header) => String(header || '').trim().toLowerCase() === 'email'
      ),
    [headers]
  );

  const isUsuariosSheet = useMemo(
    () => String(selectedSheet || '').trim().toLowerCase() === 'usuarios',
    [selectedSheet]
  );

  const isRolesSheet = useMemo(
    () => String(selectedSheet || '').trim().toLowerCase() === ROLES_SHEET_NAME.toLowerCase(),
    [selectedSheet]
  );

  const isUserPasswordsSheet = useMemo(
    () => String(selectedSheet || '').trim().toLowerCase() === USER_PASSWORDS_SHEET_NAME.toLowerCase(),
    [selectedSheet]
  );

  const highlightedDateFieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
      borderRadius: 1.5,
    },
    '& .MuiInputLabel-root': {
      color: 'secondary.main',
      fontWeight: 700,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'secondary.main',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'secondary.main',
      borderWidth: 1.5,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'secondary.dark',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'secondary.main',
      borderWidth: 2,
    },
    '& .MuiFormHelperText-root': {
      color: 'secondary.dark',
      fontWeight: 500,
      mx: 0.25,
    },
  }), []);

  const authLocationScopes = useMemo(() => {
    if (!Array.isArray(authClaims?.locationScopes)) {
      return [];
    }

    return authClaims.locationScopes
      .map((location, index) => ({
        index,
        state: String(location?.state || '').trim(),
        city: String(location?.city || '').trim(),
        churchName: String(location?.churchName || '').trim(),
        country: String(location?.country || '').trim(),
      }))
      .filter((location) => (
        location.state || location.city || location.churchName || location.country
      ));
  }, [authClaims]);

  useEffect(() => {
    if (authLocationScopes.length <= 1) {
      setActiveLocationTab(authLocationScopes.length === 1 ? 'scope-0' : 'all');
      return;
    }

    setActiveLocationTab((previous) => {
      if (previous === 'all') {
        return previous;
      }

      const targetIndex = Number(String(previous || '').replace('scope-', ''));
      return Number.isInteger(targetIndex) && authLocationScopes[targetIndex] ? previous : 'all';
    });
  }, [authLocationScopes]);

  const isReferenceValueSheet = useMemo(() => {
    const normalizedSheetName = String(selectedSheet || '').trim().toLowerCase();
    return REFERENCE_SHEET_NAMES.some(
      (sheetName) => String(sheetName || '').trim().toLowerCase() === normalizedSheetName
    );
  }, [selectedSheet]);

  const findHeaderIndex = useCallback(
    (targetName) =>
      headers.findIndex(
        (header) => String(header || '').trim().toLowerCase() === targetName
      ),
    [headers]
  );

  const locationColumnIndexes = useMemo(
    () => getLocationColumnIndexes(headers),
    [headers]
  );

  const usuariosLocationFieldIndexes = useMemo(() => new Set(
    [
      locationColumnIndexes.stateIndex,
      locationColumnIndexes.cityIndex,
      locationColumnIndexes.churchNameIndex,
      locationColumnIndexes.countryIndex,
    ].filter((index) => index >= 0)
  ), [locationColumnIndexes]);

  const hiddenUsuariosUiFieldIndexes = useMemo(() => new Set(
    isUsuariosSheet
      ? headers.reduce((accumulator, header, index) => {
        if (isHiddenUsuariosUiHeader(header)) {
          accumulator.push(index);
        }

        return accumulator;
      }, [])
      : []
  ), [headers, isUsuariosSheet]);

  const shouldHideUsuariosFieldFromUi = useCallback(
    (index) => isUsuariosSheet && (
      usuariosLocationFieldIndexes.has(index)
      || hiddenUsuariosUiFieldIndexes.has(index)
    ),
    [hiddenUsuariosUiFieldIndexes, isUsuariosSheet, usuariosLocationFieldIndexes]
  );

  const visibleColumnManagementHeaders = useMemo(
    () => headers.filter((header) => !(isUsuariosSheet && isHiddenUsuariosUiHeader(header))),
    [headers, isUsuariosSheet]
  );

  useEffect(() => {
    if (!selectedColumnName || !isUsuariosSheet || !isHiddenUsuariosUiHeader(selectedColumnName)) {
      return;
    }

    setSelectedColumnName(visibleColumnManagementHeaders[0] || '');
  }, [isUsuariosSheet, selectedColumnName, setSelectedColumnName, visibleColumnManagementHeaders]);

  const draftStateCell = rowInputs[locationColumnIndexes.stateIndex];
  const draftCityCell = rowInputs[locationColumnIndexes.cityIndex];
  const draftChurchCell = rowInputs[locationColumnIndexes.churchNameIndex];
  const draftCountryCell = rowInputs[locationColumnIndexes.countryIndex];
  const parsedRowInputLocations = useMemo(() => {
    if (!isUsuariosSheet) {
      return [];
    }
    // Name/email/phone edits do not change the location controls or their menus.
    const locationRow = [];
    locationRow[locationColumnIndexes.stateIndex] = draftStateCell;
    locationRow[locationColumnIndexes.cityIndex] = draftCityCell;
    locationRow[locationColumnIndexes.churchNameIndex] = draftChurchCell;
    locationRow[locationColumnIndexes.countryIndex] = draftCountryCell;
    return getIndexedLocationsFromRow(headers, locationRow);
  }, [headers, isUsuariosSheet, locationColumnIndexes, draftStateCell, draftCityCell, draftChurchCell, draftCountryCell]);

  useEffect(() => {
    if (!isUsuariosSheet) {
      setLocationDraftCount(1);
      return;
    }

    setLocationDraftCount((previous) => Math.max(previous, parsedRowInputLocations.length, 1));
  }, [isUsuariosSheet, parsedRowInputLocations.length]);

  const rowInputLocations = useMemo(() => {
    if (!isUsuariosSheet) {
      return [];
    }

    const nextLocations = [...parsedRowInputLocations];
    const targetCount = Math.max(locationDraftCount, parsedRowInputLocations.length, 1);

    while (nextLocations.length < targetCount) {
      nextLocations.push({
        ...EMPTY_LOCATION_ENTRY,
        index: nextLocations.length,
      });
    }

    return nextLocations;
  }, [isUsuariosSheet, locationDraftCount, parsedRowInputLocations]);

  const rowInputLocationErrors = useMemo(() => rowInputLocations.map((location) => ({
    state: !String(location?.state || '').trim(),
    city: !String(location?.city || '').trim(),
    country: !String(location?.country || '').trim(),
  })), [rowInputLocations]);

  const rowInputsForSubmit = useMemo(() => {
    if (!isUsuariosSheet) {
      return rowInputs;
    }

    return rowInputs.map((value, index) => {
      if (!hiddenUsuariosUiFieldIndexes.has(index)) {
        return value;
      }

      return String(value || '').trim() || getHiddenUsuariosUiDefaultValue(headers[index]);
    });
  }, [headers, hiddenUsuariosUiFieldIndexes, isUsuariosSheet, rowInputs]);

  const hasLocationDraftErrors = useMemo(
    () => rowInputLocationErrors.some((entry) => entry.state || entry.city || entry.country),
    [rowInputLocationErrors]
  );

  const writeUsuariosLocationsToDraft = useCallback((locations) => {
    setRowDraft((previous) => {
      const baseRow = [...previous];

      while (baseRow.length < headers.length) {
        baseRow.push('');
      }

      return applyIndexedLocationsToRowData(headers, baseRow, locations);
    });
  }, [headers, setRowDraft]);

  const updateLocationDraft = useCallback((locationIndex, field, value) => {
    const nextLocations = rowInputLocations.map((location, index) => (
      index === locationIndex
        ? { ...location, [field]: value }
        : location
    ));

    writeUsuariosLocationsToDraft(nextLocations);
  }, [rowInputLocations, writeUsuariosLocationsToDraft]);

  const addLocationDraft = useCallback(() => {
    setLocationDraftCount((previous) => previous + 1);
  }, []);

  const removeLocationDraft = useCallback((locationIndex) => {
    const remainingLocations = rowInputLocations.filter((_, index) => index !== locationIndex);
    setLocationDraftCount(Math.max(remainingLocations.length, 1));
    writeUsuariosLocationsToDraft(remainingLocations.length > 0 ? remainingLocations : [{ ...EMPTY_LOCATION_ENTRY }]);
  }, [rowInputLocations, writeUsuariosLocationsToDraft]);

  const selectedRegularUserScope = useMemo(() => {
    if (!isReadOnlyUser) {
      return null;
    }

    if (activeLocationTab === 'all') {
      return null;
    }

    const locationIndex = Number(String(activeLocationTab || '').replace('scope-', ''));
    return Number.isInteger(locationIndex) ? authLocationScopes[locationIndex] || null : null;
  }, [activeLocationTab, authLocationScopes, isReadOnlyUser]);

  const newUserRequestScope = useMemo(() => {
    if (selectedRegularUserScope) {
      return selectedRegularUserScope;
    }

    return authLocationScopes.length === 1 ? authLocationScopes[0] : null;
  }, [authLocationScopes, selectedRegularUserScope]);

  const nombreColumnIndex = useMemo(() => findHeaderIndex('nombre'), [findHeaderIndex]);
  const apellidoColumnIndex = useMemo(() => findHeaderIndex('apellido'), [findHeaderIndex]);
  const telefonoColumnIndex = useMemo(() => findHeaderIndex('telefono'), [findHeaderIndex]);
  const idiomaColumnIndex = useMemo(() => findHeaderIndex('idioma'), [findHeaderIndex]);

  const roleNameColumnIndex = useMemo(
    () => findHeaderIndex('nombre'),
    [findHeaderIndex]
  );

  const managedValueColumnIndex = useMemo(() => {
    if (isRolesSheet) {
      return roleNameColumnIndex;
    }

    if (!isReferenceValueSheet) {
      return -1;
    }

    return findHeaderIndex(String(selectedSheet || '').trim().toLowerCase());
  }, [findHeaderIndex, isReferenceValueSheet, isRolesSheet, roleNameColumnIndex, selectedSheet]);

  useEffect(() => {
    if (!accessToken || !isMainAdminUser || !isUsuariosSheet || emailColumnIndex < 0) {
      setPasswordStatusByEmail({});
      return;
    }

    let cancelled = false;

    async function loadPasswordStatuses() {
      const emails = rows
        .map((row) => String(row?.[emailColumnIndex] || '').trim().toLowerCase())
        .filter(Boolean);

      if (emails.length === 0) {
        if (!cancelled) {
          setPasswordStatusByEmail({});
        }
        return;
      }

      try {
        const result = await fetchUserPasswordStatuses(accessToken, emails);

        if (!cancelled) {
          setPasswordStatusByEmail(result.statuses || {});
        }
      } catch {
        if (!cancelled) {
          setPasswordStatusByEmail({});
        }
      }
    }

    loadPasswordStatuses();

    return () => {
      cancelled = true;
    };
  }, [accessToken, emailColumnIndex, isMainAdminUser, isUsuariosSheet, rows]);

  const usuariosFilterValueOptionsByField = useMemo(() => {
    if (!isUsuariosSheet) {
      return {};
    }

    return headers.reduce((accumulator, header, columnIndex) => {
      const field = `col_${columnIndex}`;
      const valueOptions = Array.from(
        new Set(
          rows
            .map((row) => (isPhoneColumnLabel(header)
              ? formatPhoneValueForRow(header, row?.[columnIndex], headers, row)
              : String(row?.[columnIndex] || '').trim()))
            .filter(Boolean)
        )
      ).sort((leftValue, rightValue) => leftValue.localeCompare(rightValue, 'es', { sensitivity: 'base' }));

      if (valueOptions.length > 0) {
        accumulator[field] = valueOptions;
      }

      return accumulator;
    }, {});
  }, [headers, isUsuariosSheet, rows]);

  useEffect(() => {
    setGridFilterModel(EMPTY_GRID_FILTER_MODEL);
    setActiveGridFilterItems([]);
    setGlobalSearch('');
    setGridPaginationModel((previous) => ({ ...previous, page: 0 }));
    lastInteractedGridFilterFieldRef.current = '';
  }, [selectedSheet, accessToken]);

  const changeGlobalSearch = useCallback((value) => {
    setGlobalSearch(value);
    setGridPaginationModel((previous) => previous.page === 0 ? previous : { ...previous, page: 0 });
  }, []);

  const commitGridFilterItems = useCallback((nextPrimaryItem, fieldToClear = '') => {
    setActiveGridFilterItems((previousItems) => {
      const itemsByField = previousItems.reduce((accumulator, item) => {
        if (item?.field) {
          accumulator[item.field] = item;
        }

        return accumulator;
      }, {});

      if (nextPrimaryItem?.field) {
        if (isGridFilterItemActive(nextPrimaryItem)) {
          itemsByField[nextPrimaryItem.field] = {
            ...nextPrimaryItem,
            id: nextPrimaryItem.id || nextPrimaryItem.field,
          };
        } else {
          delete itemsByField[nextPrimaryItem.field];
        }
      } else if (fieldToClear) {
        delete itemsByField[fieldToClear];
      }

      return Object.values(itemsByField);
    });
  }, []);

  const handleGridFilterModelChange = useCallback((nextModel) => {
    const nextPrimaryItem = getPrimaryGridFilterItem(nextModel);

    if (nextPrimaryItem?.field) {
      lastInteractedGridFilterFieldRef.current = nextPrimaryItem.field;
    }

    setGridFilterModel(nextPrimaryItem ? { items: [nextPrimaryItem] } : EMPTY_GRID_FILTER_MODEL);
  }, []);

  const clearGridFilterForField = useCallback((field) => {
    setActiveGridFilterItems((previousItems) => previousItems.filter((item) => item.field !== field));

    if (lastInteractedGridFilterFieldRef.current === field) {
      lastInteractedGridFilterFieldRef.current = '';
      setGridFilterModel(EMPTY_GRID_FILTER_MODEL);
    }
  }, []);

  const clearAllGridFilters = useCallback(() => {
    lastInteractedGridFilterFieldRef.current = '';
    setActiveGridFilterItems([]);
    setGridFilterModel(EMPTY_GRID_FILTER_MODEL);
    changeGlobalSearch('');
  }, [changeGlobalSearch]);

  const draftGridFilterItem = useMemo(
    () => getPrimaryGridFilterItem(gridFilterModel),
    [gridFilterModel]
  );

  const draftGridFilterField = draftGridFilterItem?.field || lastInteractedGridFilterFieldRef.current || '';

  const activeGridFilterItemForDraftField = useMemo(
    () => activeGridFilterItems.find((item) => item.field === draftGridFilterField) || null,
    [activeGridFilterItems, draftGridFilterField]
  );

  const hasPendingGridFilterChanges = useMemo(() => {
    if (!draftGridFilterField) {
      return false;
    }

    if (!draftGridFilterItem) {
      return Boolean(activeGridFilterItemForDraftField);
    }

    return !areGridFilterItemsEquivalent(draftGridFilterItem, activeGridFilterItemForDraftField);
  }, [activeGridFilterItemForDraftField, draftGridFilterField, draftGridFilterItem]);

  const applyCurrentGridFilterDraft = useCallback((overridePrimaryItem = null) => {
    const nextPrimaryItem = overridePrimaryItem || getPrimaryGridFilterItem(gridFilterModel);
    const fieldToClear = nextPrimaryItem?.field ? '' : lastInteractedGridFilterFieldRef.current;

    commitGridFilterItems(nextPrimaryItem, fieldToClear);
  }, [commitGridFilterItems, gridFilterModel]);

  const activeLocationStatusFilter = activeGridFilterItems.find((item) => item.field === LOCATION_REVIEW_STATUS_FIELD);
  const applyLocationStatusQuickFilter = useCallback((value) => {
    if (!value) {
      clearGridFilterForField(LOCATION_REVIEW_STATUS_FIELD);
      return;
    }
    const item = { field: LOCATION_REVIEW_STATUS_FIELD, operator: 'is', value, id: LOCATION_REVIEW_STATUS_FIELD };
    lastInteractedGridFilterFieldRef.current = LOCATION_REVIEW_STATUS_FIELD;
    setGridFilterModel({ items: [item] });
    commitGridFilterItems(item);
  }, [clearGridFilterForField, commitGridFilterItems]);

  const requestSheetColumnVisibilityModel = useMemo(() => {
    if (!isSelectedRequestSheet) {
      return {};
    }

    return headers.reduce((accumulator, header, index) => {
      const normalizedHeader = normalizeHeaderName(header);

      if (
        normalizedHeader.startsWith(REQUEST_REQUESTER_HEADER_PREFIX)
        || (
          normalizedHeader.startsWith(REQUEST_TARGET_HEADER_PREFIX)
          && !REQUEST_VISIBLE_TARGET_HEADERS.has(normalizedHeader)
        )
        || normalizedHeader.endsWith(REQUEST_CURRENT_VALUE_SUFFIX)
      ) {
        accumulator[`col_${index}`] = false;
      }

      return accumulator;
    }, {});
  }, [headers, isSelectedRequestSheet]);

  const regularUserUsuariosColumnVisibilityModel = useMemo(() => {
    if (!isReadOnlyUser || !isUsuariosSheet) {
      return {};
    }

    return headers.reduce((accumulator, header, index) => {
      if (REGULAR_USER_DEFAULT_HIDDEN_LOCATION_HEADERS.has(normalizeHeaderName(header))) {
        accumulator[`col_${index}`] = false;
      }

      return accumulator;
    }, {});
  }, [headers, isReadOnlyUser, isUsuariosSheet]);

  const defaultColumnVisibilityModel = useMemo(() => {
    if (isSelectedRequestSheet) {
      return requestSheetColumnVisibilityModel;
    }

    if (isReadOnlyUser && isUsuariosSheet) {
      return regularUserUsuariosColumnVisibilityModel;
    }

    return {};
  }, [
    isReadOnlyUser,
    isSelectedRequestSheet,
    isUsuariosSheet,
    regularUserUsuariosColumnVisibilityModel,
    requestSheetColumnVisibilityModel,
  ]);

  useEffect(() => {
    setGridColumnVisibilityModel(defaultColumnVisibilityModel);
  }, [defaultColumnVisibilityModel, selectedSheet]);

  const quickHideLocationColumnFieldKeys = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (QUICK_HIDE_LOCATION_HEADERS.has(normalizeHeaderName(header))) {
        accumulator.push(`col_${index}`);
      }

      return accumulator;
    }, []),
    [headers]
  );

  useEffect(() => {
    if (!isMainAdminUser || quickHideLocationColumnFieldKeys.length === 0) {
      setHideLocationColumnsQuick(false);
    }
  }, [isMainAdminUser, quickHideLocationColumnFieldKeys.length]);

  const effectiveGridColumnVisibilityModel = useMemo(() => {
    if (!hideLocationColumnsQuick || quickHideLocationColumnFieldKeys.length === 0) {
      return gridColumnVisibilityModel;
    }

    const forcedHiddenColumns = quickHideLocationColumnFieldKeys.reduce((accumulator, field) => {
      accumulator[field] = false;
      return accumulator;
    }, {});

    return {
      ...gridColumnVisibilityModel,
      ...forcedHiddenColumns,
    };
  }, [gridColumnVisibilityModel, hideLocationColumnsQuick, quickHideLocationColumnFieldKeys]);

  const requesterInfoRow = useMemo(() => {
    if (requesterInfoDialog.rowIndex === null) {
      return null;
    }

    return rows[requesterInfoDialog.rowIndex] || null;
  }, [requesterInfoDialog.rowIndex, rows]);

  const requesterInfoEntries = useMemo(() => {
    if (!isSelectedRequestSheet || !requesterInfoRow) {
      return [];
    }

    return headers.reduce((accumulator, header, index) => {
      const normalizedHeader = normalizeHeaderName(header);

      if (
        normalizedHeader === normalizeHeaderName(REQUEST_SUBMITTED_AT_HEADER)
        || normalizedHeader.startsWith(REQUEST_REQUESTER_HEADER_PREFIX)
      ) {
        accumulator.push({
          label: normalizedHeader === normalizeHeaderName(REQUEST_SUBMITTED_AT_HEADER)
            ? formatRequestDetailLabel(header)
            : formatRequestDetailLabel(header, { stripRequesterPrefix: true }),
          value: formatRowDisplayValue(header, requesterInfoRow[index], headers, requesterInfoRow),
        });
      }

      return accumulator;
    }, []);
  }, [headers, isSelectedRequestSheet, requesterInfoRow]);

  const locationDetailsRows = useMemo(() => {
    if (locationDetailsDialog.rowIndex === null) {
      return [];
    }

    return getIndexedLocationsFromRow(headers, rows[locationDetailsDialog.rowIndex] || []);
  }, [headers, locationDetailsDialog.rowIndex, rows]);

  const requestOriginalEntries = useMemo(() => {
    if (!isSelectedRequestSheet || !requesterInfoRow) {
      return [];
    }

    return headers.reduce((accumulator, header, index) => {
      const normalizedHeader = normalizeHeaderName(header);

      if (
        normalizedHeader.startsWith(REQUEST_TARGET_HEADER_PREFIX)
        || normalizedHeader.endsWith(REQUEST_CURRENT_VALUE_SUFFIX)
      ) {
        accumulator.push({
          label: formatRequestDetailLabel(header, {
            stripTargetPrefix: true,
            stripCurrentSuffix: true,
          }),
          value: formatRowDisplayValue(header, requesterInfoRow[index], headers, requesterInfoRow),
        });
      }

      return accumulator;
    }, []);
  }, [headers, isSelectedRequestSheet, requesterInfoRow]);

  const requestChangeEntries = useMemo(() => {
    if (!isSelectedRequestSheet || !requesterInfoRow) {
      return [];
    }

    const normalizeChangeLabel = (header) => String(header || '')
      .replace(/^Usuario\s*-\s*/i, '')
      .replace(/\s+(actual|solicitado|solicitada|nuevo|nueva)$/i, '')
      .trim();
    const originalByLabel = new Map(
      requestOriginalEntries.map((entry) => [normalizeHeaderName(entry.label), entry.value])
    );
    const isNewUserRequest = selectedSheet === REQUEST_NEW_USER_SHEET_NAME;

    return headers.reduce((accumulator, header, index) => {
      const normalizedHeader = normalizeHeaderName(header);
      const isMetadata = normalizedHeader === normalizeHeaderName(REQUEST_SUBMITTED_AT_HEADER)
        || normalizedHeader.startsWith(REQUEST_REQUESTER_HEADER_PREFIX);
      const isOriginal = normalizedHeader.startsWith(REQUEST_TARGET_HEADER_PREFIX)
        || normalizedHeader.endsWith(REQUEST_CURRENT_VALUE_SUFFIX);
      const isExplicitChange = /\s(solicitado|solicitada|nuevo|nueva)$/i.test(String(header || '').trim())
        || normalizedHeader === 'nota';

      if (isMetadata || isOriginal || (!isNewUserRequest && !isExplicitChange)) {
        return accumulator;
      }

      const label = normalizeChangeLabel(header);
      const requestedValue = formatRowDisplayValue(header, requesterInfoRow[index], headers, requesterInfoRow);
      const originalValue = originalByLabel.get(normalizeHeaderName(label)) || '-';
      const hasKnownOriginal = originalValue !== '-';
      const changed = normalizeHeaderName(originalValue) !== normalizeHeaderName(requestedValue)
        && (hasKnownOriginal || isNewUserRequest || normalizedHeader === 'nota');

      accumulator.push({ label, originalValue, requestedValue, changed });
      return accumulator;
    }, []);
  }, [headers, isSelectedRequestSheet, requestOriginalEntries, requesterInfoRow, selectedSheet]);

  const requestTargetEntries = useMemo(() => {
    if (!isSelectedRequestSheet || !requesterInfoRow) {
      return [];
    }

    const isNewUserRequest = selectedSheet === REQUEST_NEW_USER_SHEET_NAME;
    const wantedLabels = new Set(['nombre', 'apellido', 'email', 'telefono', 'labor/rol', 'idioma', 'sedes']);
    const entriesByLabel = new Map();

    headers.forEach((header, index) => {
      const normalizedHeader = normalizeHeaderName(header);
      const isTargetHeader = normalizedHeader.startsWith(REQUEST_TARGET_HEADER_PREFIX);
      const isNewUserIdentityHeader = isNewUserRequest && wantedLabels.has(normalizedHeader);

      if (!isTargetHeader && !isNewUserIdentityHeader) {
        return;
      }

      const label = formatRequestDetailLabel(header, {
        stripTargetPrefix: isTargetHeader,
        stripCurrentSuffix: true,
      });
      const normalizedLabel = normalizeHeaderName(label);

      if (!wantedLabels.has(normalizedLabel) || entriesByLabel.has(normalizedLabel)) {
        return;
      }

      entriesByLabel.set(normalizedLabel, {
        label,
        value: formatRowDisplayValue(header, requesterInfoRow[index], headers, requesterInfoRow),
      });
    });

    return ['nombre', 'apellido', 'email', 'telefono', 'labor/rol', 'idioma', 'sedes']
      .map((label) => entriesByLabel.get(label))
      .filter(Boolean);
  }, [headers, isSelectedRequestSheet, requesterInfoRow, selectedSheet]);

  const gridColumns = useMemo(() => {
    const baseColumns = headers.reduce((accumulator, header, index) => {
      if (isUsuariosSheet && isHiddenUsuariosUiHeader(header)) {
        return accumulator;
      }

      const field = `col_${index}`;
      const valueOptions = usuariosFilterValueOptionsByField[field] || [];
      const isLocationSummaryColumn = isUsuariosSheet && usuariosLocationFieldIndexes.has(index);
      const isPhoneColumn = isPhoneColumnLabel(header);
      const roleNotificationSetting = isRolesSheet ? getRoleNotificationSetting(header) : null;
      const filterOperators = getGridStringOperators().map((operator) => {
        if (operator.requiresFilterValue === false || operator.value === 'isAnyOf') {
          return operator;
        }

        return {
          ...operator,
          InputComponent: isUsuariosSheet && valueOptions.length > 0
            ? UsuariosFilterAutocompleteInput
            : GridFilterTextInput,
          InputComponentProps: {
            isPhoneColumn,
            ...(isUsuariosSheet && valueOptions.length > 0
              ? { valueOptions }
              : {}),
            isApplyDisabled: !hasPendingGridFilterChanges,
            onApplyFilter: applyCurrentGridFilterDraft,
          },
        };
      });

      accumulator.push({
        field,
        headerName: isSelectedRequestSheet && REQUEST_VISIBLE_TARGET_HEADERS.has(normalizeHeaderName(header))
          ? formatRequestDetailLabel(header, { stripTargetPrefix: true })
          : (getColumnDisplayName(header, selectedSheet) || `Columna ${index}`),
        description: roleNotificationSetting?.description,
        renderHeader: roleNotificationSetting
          ? () => (
            <Tooltip title={roleNotificationSetting.description} arrow>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                component="span"
                tabIndex={0}
                aria-label={`${roleNotificationSetting.label}: ${roleNotificationSetting.description}`}
              >
                <span>{roleNotificationSetting.label}</span>
                <Info size={14} aria-hidden="true" />
              </Stack>
            </Tooltip>
          )
          : undefined,
        flex: 1,
        minWidth: roleNotificationSetting ? 205 : (isPhoneColumn ? 190 : (isUsuariosSheet && index === locationColumnIndexes.stateIndex ? 170 : 140)),
        filterOperators,
        sortComparator: isDateLikeColumnLabel(header)
          ? (leftValue, rightValue) => compareDateValues(leftValue, rightValue)
          : undefined,
        renderCell: isLocationSummaryColumn
          ? (params) => {
            const sourceRow = rows[params.row.sourceRowIndex] || [];
            const indexedLocations = getIndexedLocationsFromRow(headers, sourceRow);
            const summary = summarizeLocationCell(sourceRow[index]);

            if (isReadOnlyUser && selectedRegularUserScope && params.row.locationScope) {
              if (index === locationColumnIndexes.stateIndex) {
                return params.row.locationScope.state || '';
              }

              if (index === locationColumnIndexes.cityIndex) {
                return params.row.locationScope.city || '';
              }

              if (index === locationColumnIndexes.churchNameIndex) {
                return params.row.locationScope.churchName || '';
              }

              if (index === locationColumnIndexes.countryIndex) {
                return params.row.locationScope.country || '';
              }
            }

            if (indexedLocations.length <= 1) {
              return summary.label || '';
            }

            return (
              <Tooltip
                title={<Box sx={{ whiteSpace: 'pre-line' }}>{formatLocationScopeTooltip(indexedLocations)}</Box>}
                arrow
              >
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  onClick={() => setLocationDetailsDialog({ open: true, rowIndex: params.row.sourceRowIndex })}
                >
                  {`${indexedLocations.length}+`}
                </Button>
              </Tooltip>
            );
          }
          : (isDateLikeColumnLabel(header)
            ? (params) => formatDateValueForLabel(header, params.row?.[field], '')
            : undefined),
      });

      return accumulator;
    }, []);

    // Move the display column only; col_<index> still points to its original row value.
    if (isUsuariosSheet) {
      const countryColumnIndex = baseColumns.findIndex(
        (column) => column.field === `col_${locationColumnIndexes.countryIndex}`
      );
      if (countryColumnIndex > 0) {
        baseColumns.unshift(...baseColumns.splice(countryColumnIndex, 1));
      }

      baseColumns.push({
        field: LOCATION_REVIEW_STATUS_FIELD,
        headerName: 'Datos correctos',
        description: 'Confirmación de los datos de este usuario, con fecha y responsable por sede.',
        minWidth: 285,
        flex: 1.4,
        sortable: false,
        filterable: true,
        filterOperators: [{
          label: 'es',
          value: 'is',
          getApplyFilterFn: (item) => item.value ? (value) => doesLocationReviewMatchFilter(value, item) : null,
          getValueAsString: getLocationReviewFilterLabel,
          InputComponent: LocationStatusFilterInput,
          InputComponentProps: {
            isApplyDisabled: !hasPendingGridFilterChanges,
            onApplyFilter: applyCurrentGridFilterDraft,
          },
        }],
        renderCell: (params) => {
          const sourceRow = rows[params.row.sourceRowIndex] || [];
          const rowLocations = params.row.locationScope
            ? [params.row.locationScope]
            : getIndexedLocationsFromRow(headers, sourceRow);
          const userLocations = getRelevantLocationReviewScopes(rowLocations, {
            authorizedScopes: isReadOnlyUser ? authLocationScopes : undefined,
          });
          const userStatuses = userLocationStatusIndex.get(userReviewEmailKey(sourceRow[emailColumnIndex]))?.statuses || [];
          const display = getUserLocationStatusDisplay(userLocations, userStatuses, locationStatusState);
          return (
            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{display.tooltip}</Box>} arrow>
              <Stack spacing={0.25} sx={{ justifyContent: 'center', height: 1, minWidth: 0 }}>
                <Typography variant="body2" color={display.color === 'default' ? 'text.secondary' : `${display.color}.main`} noWrap>
                  {display.label}
                </Typography>
                {display.dateLabel && <Typography variant="caption" color="text.secondary" noWrap>{display.dateLabel}</Typography>}
              </Stack>
            </Tooltip>
          );
        },
      });
    }

    if (!isReadOnlyUser && !isMainAdminUser) {
      return baseColumns;
    }

    return [
      {
        field: 'actions',
        headerName: 'Acciones',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: isReadOnlyUser
          ? (isUsuariosSheet ? (isMobileViewport ? 132 : 170) : (isMobileViewport ? 102 : 120))
          : (isSelectedRequestSheet ? (isMobileViewport ? 230 : 300) : (isUsuariosSheet ? (isMobileViewport ? 188 : 220) : (isMobileViewport ? 150 : 180))),
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (isReadOnlyUser) {
            const rowEmail = emailColumnIndex >= 0
              ? String(params.row[`col_${emailColumnIndex}`] || '').trim()
              : '';
            const pendingRequests = pendingRequestsByEmail[rowEmail.toLowerCase()] || [];

            return (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 1 }}>
                {isUsuariosSheet && pendingRequests.length > 0 && (
                  <Tooltip title={`Ver ${pendingRequests.length} solicitud(es) pendiente(s)`} arrow>
                    <span>
                      <IconButton
                        type="button"
                        color='warning'
                        aria-label={`Ver solicitudes pendientes de fila ${params.row.sourceRowIndex + 1}`}
                        disabled={!rowEmail}
                        onClick={() => openPendingRequestsDialog(rowEmail)}
                      >
                        <Info size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Solicitar cambios para este usuario" arrow>
                  <span>
                    <IconButton
                      type="button"
                      aria-label={`Solicitar cambios para fila ${params.row.sourceRowIndex + 1}`}
                      disabled={!isUsuariosSheet || loading || refreshing}
                      onClick={() => openRequestDialog(params.row.sourceRowIndex, params.row.locationIndex ?? null)}
                    >
                      <Pencil size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          }

          const rowEmail = emailColumnIndex >= 0
            ? String(params.row[`col_${emailColumnIndex}`] || '').trim()
            : '';
          const pendingRequests = pendingRequestsByEmail[rowEmail.toLowerCase()] || [];
          const normalizedEmail = rowEmail.toLowerCase();
          const passwordStatus = passwordStatusByEmail[normalizedEmail] || null;
          const passwordSummary = getPasswordStatusSummary(passwordStatus);
          const showPasswordAction = isUsuariosSheet;

          if (isSelectedRequestSheet) {
            const rowActionInProgress =
              loading
              || refreshing
              || (requestRowAction.rowIndex === params.row.sourceRowIndex && Boolean(requestRowAction.mode));

            return (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="center"
                sx={{ height: 1, py: 0.5, flexWrap: 'nowrap' }}
              >
                <Tooltip title="Ver quien hizo la solicitud" arrow>
                  <Box component="span" sx={{ display: 'inline-flex', alignSelf: 'center' }}>
                    <Button
                      type="button"
                      size="small"
                      variant="contained"
                      color="secondary"
                      aria-label={`Ver quien hizo la solicitud de la fila ${params.row.sourceRowIndex + 1}`}
                      disabled={rowActionInProgress}
                      onClick={() => openRequesterInfoDialog(params.row.sourceRowIndex)}
                      sx={{ textTransform: 'none', py: 0.5, minWidth: isMobileViewport ? 62 : 72 }}
                    >
                      Ver
                    </Button>
                  </Box>
                </Tooltip>
                <Tooltip
                  title="Marca la solicitud como ejecutada externamente y actualiza el registro interno. Esta aplicación no realiza cambios en sistemas externos."
                  arrow
                >
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                      type="button"
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircle2 size={15} />}
                      disabled={rowActionInProgress}
                      sx={{ textTransform: 'none', py: 0.5, minWidth: isMobileViewport ? 92 : 108 }}
                      onClick={() => {
                        if (selectedSheet === REQUEST_NEW_USER_SHEET_NAME) {
                          openApplyNewUserDialog(params.row.sourceRowIndex);
                          return;
                        }

                        runRequestRowAction('apply', params.row.sourceRowIndex);
                      }}
                    >
                      Ejecutado
                    </Button>
                  </Box>
                </Tooltip>
                <Button
                  type="button"
                  size="small"
                  color="error"
                  variant="contained"
                  disabled={rowActionInProgress}
                  sx={{ textTransform: 'none', py: 0.5, minWidth: isMobileViewport ? 68 : 86 }}
                  onClick={() => runRequestRowAction('dismiss', params.row.sourceRowIndex)}
                >
                  Descartar
                </Button>
              </Stack>
            );
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 1 }}>
              {isUsuariosSheet && pendingRequests.length > 0 && (
                <Tooltip title={`Ver ${pendingRequests.length} solicitud(es) pendiente(s)`} arrow>
                  <span>
                    <IconButton
                      type="button"
                      color='warning'
                      aria-label={`Ver solicitudes pendientes de fila ${params.row.sourceRowIndex + 1}`}
                      disabled={!rowEmail}
                      onClick={() => openPendingRequestsDialog(rowEmail)}
                    >
                      <Info size={16}/>
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Editar fila" arrow>
                <span>
                  <IconButton
                    type="button"
                    aria-label={`Editar fila ${params.row.sourceRowIndex + 1}`}
                    disabled={loading || refreshing}
                    onClick={() => openEditDialog(params.row.sourceRowIndex)}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              {showPasswordAction && (
                <Tooltip
                  title={passwordSummary.tooltip}
                  arrow
                >
                  <span>
                    <IconButton
                      type="button"
                      aria-label={`Gestionar contraseña de fila ${params.row.sourceRowIndex + 1}`}
                      color={passwordSummary.buttonColor}
                      disabled={loading || refreshing || !rowEmail}
                      onClick={() => openPasswordDialog(
                        rowEmail,
                        getLocationLabelByRowIndex(params.row.sourceRowIndex)
                      )}
                    >
                      <KeyRound size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Eliminar fila" arrow>
                <span>
                  <IconButton
                    type="button"
                    color="error"
                    aria-label={`Eliminar fila ${params.row.sourceRowIndex + 1}`}
                    disabled={loading || refreshing}
                    onClick={() => openDeleteRowDialog(params.row.sourceRowIndex)}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
      ...baseColumns,
    ];
  }, [
    headers,
    emailColumnIndex,
    isMainAdminUser,
    isMobileViewport,
    isReadOnlyUser,
    isRolesSheet,
    isSelectedRequestSheet,
    isUsuariosSheet,
    loading,
    refreshing,
    openApplyNewUserDialog,
    openPendingRequestsDialog,
    openDeleteRowDialog,
    openEditDialog,
    getLocationLabelByRowIndex,
    openPasswordDialog,
    openRequesterInfoDialog,
    openRequestDialog,
    pendingRequestsByEmail,
    passwordStatusByEmail,
    requestRowAction,
    runRequestRowAction,
    selectedSheet,
    rows,
    selectedRegularUserScope,
    usuariosFilterValueOptionsByField,
    usuariosLocationFieldIndexes,
    hasPendingGridFilterChanges,
    applyCurrentGridFilterDraft,
    locationColumnIndexes.churchNameIndex,
    locationColumnIndexes.cityIndex,
    locationColumnIndexes.countryIndex,
    locationColumnIndexes.stateIndex,
    locationStatusState,
    userLocationStatusIndex,
    authLocationScopes,
  ]);

  const locationReviewIndex = useMemo(() => createLocationReviewIndex([]), []);

  const rawGridRows = useMemo(() => rows.map((row, rowIndex) => {
    const indexedLocations = getIndexedLocationsFromRow(headers, row);
    const matchedScope = selectedRegularUserScope
      ? findMatchingLocationScope(indexedLocations, selectedRegularUserScope)
      : null;
    const mappedRow = {
      id: rowIndex,
      sourceRowIndex: rowIndex,
      locationScope: matchedScope,
      locationScopes: indexedLocations,
      [LOCATION_REVIEW_STATUS_FIELD]: getUserLocationReviewValue(getRelevantLocationReviewScopes(indexedLocations, {
        selectedLocationScope: selectedRegularUserScope,
        authorizedScopes: isReadOnlyUser ? authLocationScopes : undefined,
      }), userLocationStatusIndex.get(userReviewEmailKey(row[emailColumnIndex]))?.review || locationReviewIndex, locationStatusState),
    };

    headers.forEach((header, columnIndexValue) => {
      mappedRow[`col_${columnIndexValue}`] = isPhoneColumnLabel(header)
        ? formatPhoneValueForRow(header, row[columnIndexValue], headers, row)
        : row[columnIndexValue] || '';
    });

    if (locationColumnIndexes.stateIndex >= 0) {
      mappedRow[`col_${locationColumnIndexes.stateIndex}`] = matchedScope
        ? matchedScope.state || ''
        : buildLocationCellFilterText(row[locationColumnIndexes.stateIndex]);
    }

    if (locationColumnIndexes.cityIndex >= 0) {
      mappedRow[`col_${locationColumnIndexes.cityIndex}`] = matchedScope
        ? matchedScope.city || ''
        : buildLocationCellFilterText(row[locationColumnIndexes.cityIndex]);
    }

    if (locationColumnIndexes.churchNameIndex >= 0) {
      mappedRow[`col_${locationColumnIndexes.churchNameIndex}`] = matchedScope
        ? matchedScope.churchName || ''
        : buildLocationCellFilterText(row[locationColumnIndexes.churchNameIndex]);
    }

    if (locationColumnIndexes.countryIndex >= 0) {
      mappedRow[`col_${locationColumnIndexes.countryIndex}`] = matchedScope
        ? matchedScope.country || ''
        : buildLocationCellFilterText(row[locationColumnIndexes.countryIndex]);
    }

    return mappedRow;
  }), [
    headers,
    locationColumnIndexes.churchNameIndex,
    locationColumnIndexes.cityIndex,
    locationColumnIndexes.countryIndex,
    locationColumnIndexes.stateIndex,
    rows,
    selectedRegularUserScope,
    authLocationScopes,
    isReadOnlyUser,
    emailColumnIndex,
    locationReviewIndex,
    userLocationStatusIndex,
    locationStatusState,
  ]);

  const scopeFilteredGridRows = useMemo(() => {
    if (!isReadOnlyUser || !isUsuariosSheet || !selectedRegularUserScope) {
      return rawGridRows;
    }

    return rawGridRows.filter((row) => (
      rowContainsLocationScope(headers, rows[row.sourceRowIndex] || [], selectedRegularUserScope)
    ));
  }, [headers, isReadOnlyUser, isUsuariosSheet, rawGridRows, rows, selectedRegularUserScope]);

  const globalSearchIndex = useMemo(() => {
    if (!isUsuariosSheet) return new Map();
    const fields = headers.flatMap((header, index) => isHiddenUsuariosUiHeader(header) ? [] : [{ field: `col_${index}`, isPhone: isPhoneColumnLabel(header) }]);
    const phoneFields = fields.filter(({ isPhone }) => isPhone);
    return new Map(scopeFilteredGridRows.map((row) => {
      const review = row[LOCATION_REVIEW_STATUS_FIELD];
      const reviewLabel = review?.available && review.states.length
        ? (review.states.every((status) => status === 'up_to_date') ? 'Todo está al día' : 'Requiere revisión') : '';
      return [row.id, createGridSearchIndex(
        [...fields.map(({ field }) => row[field]), reviewLabel],
        phoneFields.map(({ field }) => row[field]),
      )];
    }));
  }, [headers, isUsuariosSheet, scopeFilteredGridRows]);
  const globalSearchTerms = useMemo(() => isUsuariosSheet ? getGridSearchTerms(globalSearch) : [], [globalSearch, isUsuariosSheet]);

  const gridRows = useMemo(() => {
    if (activeGridFilterItems.length === 0 && globalSearchTerms.length === 0) {
      return scopeFilteredGridRows;
    }

    return scopeFilteredGridRows.filter((row) => (!globalSearchTerms.length || doesGridSearchMatch(globalSearchIndex.get(row.id), globalSearchTerms)) && activeGridFilterItems.every((filterItem) => {
      if (filterItem.field === LOCATION_REVIEW_STATUS_FIELD) {
        return doesLocationReviewMatchFilter(row[LOCATION_REVIEW_STATUS_FIELD], filterItem);
      }
      const columnIndex = Number(/^col_(\d+)$/.exec(filterItem.field)?.[1]);

      return doesGridCellMatchFilter(row[filterItem.field], filterItem, {
        isPhone: isPhoneColumnLabel(headers[columnIndex]),
      });
    }));
  }, [activeGridFilterItems, globalSearchIndex, globalSearchTerms, headers, scopeFilteredGridRows]);

  const gridInitialState = useMemo(() => ({
    pagination: {
      paginationModel: { pageSize: 100, page: 0 },
    },
  }), []);

  const laborRoleColumnIndex = useMemo(
    () =>
      headers.findIndex(
        (header) => String(header || '').trim().toLowerCase() === 'labor/rol'
      ),
    [headers]
  );

  const creadoColumnIndex = useMemo(
    () =>
      headers.findIndex(
        (header) => String(header || '').trim().toLowerCase() === CREADO_HEADER.toLowerCase()
      ),
    [headers]
  );

  const iglesiaEstadoInputIndexes = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (String(header || '').trim().toLowerCase() === 'iglesia - estado') {
        accumulator.push(index);
      }
      return accumulator;
    }, []),
    [headers]
  );

  const iglesiaCiudadInputIndexes = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (String(header || '').trim().toLowerCase() === 'iglesia - ciudad') {
        accumulator.push(index);
      }
      return accumulator;
    }, []),
    [headers]
  );

  const iglesiaNombreInputIndexes = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (String(header || '').trim().toLowerCase() === 'iglesia - nombre') {
        accumulator.push(index);
      }
      return accumulator;
    }, []),
    [headers]
  );

  const paisInputIndexes = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (String(header || '').trim().toLowerCase() === 'pais') {
        accumulator.push(index);
      }
      return accumulator;
    }, []),
    [headers]
  );

  const idiomaInputIndexes = useMemo(
    () => headers.reduce((accumulator, header, index) => {
      if (String(header || '').trim().toLowerCase() === 'idioma') {
        accumulator.push(index);
      }
      return accumulator;
    }, []),
    [headers]
  );

  const requestRoleOptions = useMemo(() => {
    const rolesFromSheet = roleOptions.filter(Boolean);

    const rolesFromUsuarios = laborRoleColumnIndex < 0
      ? []
      : Array.from(
        new Set(
          rows
            .map((row) => String(row?.[laborRoleColumnIndex] || '').trim())
            .filter(Boolean)
        )
      );

    return Array.from(new Set([...rolesFromSheet, ...rolesFromUsuarios]));
  }, [laborRoleColumnIndex, roleOptions, rows]);

  const deletingManagedValue = useMemo(() => {
    if ((!isRolesSheet && !isReferenceValueSheet) || deleteRowDialog.rowIndex === null) {
      return '';
    }

    const targetRow = rows[deleteRowDialog.rowIndex] || [];

    if (managedValueColumnIndex < 0) {
      return String(targetRow[0] || '').trim();
    }

    return String(targetRow[managedValueColumnIndex] || '').trim();
  }, [deleteRowDialog.rowIndex, isReferenceValueSheet, isRolesSheet, managedValueColumnIndex, rows]);

  const replacementManagedValueOptions = useMemo(() => {
    if ((!isRolesSheet && !isReferenceValueSheet) || managedValueColumnIndex < 0) {
      return [];
    }

    const normalizedDeletingValue = String(deletingManagedValue || '').trim().toLowerCase();
    const sourceValues = isRolesSheet
      ? roleOptions
      : Array.from(
        new Set(
          rows
            .map((row) => String(row?.[managedValueColumnIndex] || '').trim())
            .filter(Boolean)
        )
      );

    return sourceValues.filter(
      (value) => String(value || '').trim().toLowerCase() !== normalizedDeletingValue
    );
  }, [deletingManagedValue, isReferenceValueSheet, isRolesSheet, managedValueColumnIndex, roleOptions, rows]);

  const selectedRequestRow = useMemo(() => {
    if (requestDialog.rowIndex === null) {
      return null;
    }

    return rows[requestDialog.rowIndex] || null;
  }, [requestDialog.rowIndex, rows]);

  const selectedRequestLocation = useMemo(() => {
    if (!selectedRequestRow) {
      return null;
    }

    if (selectedRegularUserScope) {
      return selectedRegularUserScope;
    }

    const indexedLocations = getIndexedLocationsFromRow(headers, selectedRequestRow);

    if (indexedLocations.length === 0) {
      return null;
    }

    if (requestDialog.locationIndex === null) {
      return indexedLocations[0] || null;
    }

    return indexedLocations.find((location) => location.index === requestDialog.locationIndex) || indexedLocations[0] || null;
  }, [headers, requestDialog.locationIndex, selectedRegularUserScope, selectedRequestRow]);

  const requestTarget = useMemo(() => {
    if (!selectedRequestRow) {
      return {
        targetEmail: '',
        targetNombre: '',
        targetApellido: '',
        iglesiaEstado: '',
        iglesiaCiudad: '',
        iglesiaNombre: '',
        pais: '',
        telefono: '',
        idioma: '',
        currentRole: '',
      };
    }

    return {
      targetEmail: emailColumnIndex >= 0 ? String(selectedRequestRow[emailColumnIndex] || '').trim() : '',
      targetNombre: nombreColumnIndex >= 0 ? String(selectedRequestRow[nombreColumnIndex] || '').trim() : '',
      targetApellido: apellidoColumnIndex >= 0 ? String(selectedRequestRow[apellidoColumnIndex] || '').trim() : '',
      iglesiaEstado: selectedRequestLocation?.state || '',
      iglesiaCiudad: selectedRequestLocation?.city || '',
      iglesiaNombre: selectedRequestLocation?.churchName || '',
      pais: selectedRequestLocation?.country || '',
      telefono: telefonoColumnIndex >= 0 ? String(selectedRequestRow[telefonoColumnIndex] || '').trim() : '',
      idioma: idiomaColumnIndex >= 0 ? String(selectedRequestRow[idiomaColumnIndex] || '').trim() : '',
      currentRole: laborRoleColumnIndex >= 0 ? String(selectedRequestRow[laborRoleColumnIndex] || '').trim() : '',
    };
  }, [
    apellidoColumnIndex,
    emailColumnIndex,
    idiomaColumnIndex,
    laborRoleColumnIndex,
    nombreColumnIndex,
    selectedRequestLocation,
    selectedRequestRow,
    telefonoColumnIndex,
  ]);

  useEffect(() => {
    if (!requestDialog.open || !selectedRequestRow) {
      return;
    }

    setRowRequestInputs((previous) => ({
      ...previous,
      newCountry: requestTarget.pais,
      requestedNombre: normalizeUserRequestInput('Nombre', requestTarget.targetNombre),
      requestedApellido: normalizeUserRequestInput('Apellido', requestTarget.targetApellido),
      requestedTelefono: normalizeUserRequestInput('Telefono', requestTarget.telefono, requestTarget.pais),
      requestedEmail: normalizeUserRequestInput('Email', requestTarget.targetEmail),
      requestedIdioma: requestTarget.idioma,
    }));
  }, [requestDialog.open, requestTarget, selectedRequestRow]);

  const requestSectionDisabled =
    requestSubmitting || !requestTarget.targetEmail || !requestTarget.iglesiaEstado || !requestTarget.iglesiaCiudad || !requestTarget.pais;

  const canSubmitNewUserRequest = useMemo(
    () => Boolean(String(newUserRequestInputs.laborRol || '').trim()) && Boolean(newUserRequestScope),
    [newUserRequestInputs.laborRol, newUserRequestScope]
  );

  const usuariosRequiredFieldStatuses = useMemo(() => {
    if (!isUsuariosSheet) {
      return [];
    }

    return headers.reduce((accumulator, header, index) => {
      const normalizedHeader = normalizeHeaderName(header);

      if (
        !normalizedHeader
        || OPTIONAL_USUARIOS_FIELD_NAMES.has(normalizedHeader)
        || hiddenUsuariosUiFieldIndexes.has(index)
      ) {
        return accumulator;
      }

      const rawValue = String(rowInputs[index] || '').trim();
      const isCreatedField = index === creadoColumnIndex;
      const isComplete = isCreatedField
        ? rawValue !== '' && ['si', 'no'].includes(rawValue.toLowerCase())
        : rawValue !== '';

      accumulator.push({
        index,
        label: getColumnDisplayName(header, 'Usuarios') || `Columna ${index + 1}`,
        isComplete,
      });

      return accumulator;
    }, []);
  }, [creadoColumnIndex, headers, hiddenUsuariosUiFieldIndexes, isUsuariosSheet, rowInputs]);

  const missingUsuariosRequiredFields = useMemo(
    () => usuariosRequiredFieldStatuses.filter((field) => !field.isComplete),
    [usuariosRequiredFieldStatuses]
  );

  const missingUsuariosRequiredFieldIndexes = useMemo(
    () => new Set(missingUsuariosRequiredFields.map((field) => field.index)),
    [missingUsuariosRequiredFields]
  );

  const canSubmitUsuariosRow = useMemo(() => {
    if (!isUsuariosSheet) {
      return true;
    }

    return missingUsuariosRequiredFields.length === 0 && !hasLocationDraftErrors;
  }, [hasLocationDraftErrors, isUsuariosSheet, missingUsuariosRequiredFields.length]);

  async function submitNewUserRequest(event) {
    event.preventDefault();
    clearRequestFeedback();
    setRequestSubmitting(true);

    try {
      const scopedNewUserPayload = {
        ...newUserRequestInputs,
        nombre: normalizeUserRequestInput('Nombre', newUserRequestInputs.nombre),
        apellido: normalizeUserRequestInput('Apellido', newUserRequestInputs.apellido),
        email: normalizeUserRequestInput('Email', newUserRequestInputs.email),
        telefono: normalizeUserRequestInput('Telefono', newUserRequestInputs.telefono, newUserRequestScope?.country),
        iglesiaEstado: newUserRequestScope?.state || '',
        iglesiaCiudad: newUserRequestScope?.city || '',
        iglesiaNombre: newUserRequestScope?.churchName || '',
        pais: newUserRequestScope?.country || '',
      };

      let result;

      try {
        result = await requestNewUser(scopedNewUserPayload, accessToken);
      } catch (requestError) {
        if (isDuplicateRequestError(requestError.message)) {
          openOverrideDialog('requestNewUser', scopedNewUserPayload, requestError.message);
          return;
        } else {
          throw requestError;
        }
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud de nuevo usuario guardada.'));
      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }
      setNewUserRequestInputs({
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        laborRol: '',
        idioma: '',
      });
    } catch (requestError) {
      const duplicateExistingUserMessage = isDuplicateExistingUserError(requestError.message)
        ? 'Este usuario ya está registrado y no se puede solicitar de nuevo porque el email ya está en uso.'
        : null;

      setRequestFeedback({
        type: 'error',
        message: duplicateExistingUserMessage || toFriendlyRequestMessage(
          requestError.message,
          'No se pudo registrar la solicitud de nuevo usuario.'
        ),
      });
    } finally {
      setRequestSubmitting(false);
    }
  }

  async function submitRoleChangeRequest(event) {
    event.preventDefault();
    clearRequestFeedback();
    setRequestSubmitting(true);

    try {
      const basePayload = {
        ...requestTarget,
        requestedRole: rowRequestInputs.requestedRole,
      };

      let result;

      try {
        result = await requestRoleChange(basePayload, accessToken);
      } catch (requestError) {
        if (isDuplicateRequestError(requestError.message)) {
          openOverrideDialog('requestRoleChange', basePayload, requestError.message);
          return;
        } else {
          throw requestError;
        }
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud de cambio de rol guardada.'));
      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }
      setRowRequestInputs((previous) => ({ ...previous, requestedRole: '' }));
    } catch (requestError) {
      setRequestFeedback({
        type: 'error',
        message: toFriendlyRequestMessage(
          requestError.message,
          'No se pudo registrar la solicitud de cambio de rol.'
        ),
      });
    } finally {
      setRequestSubmitting(false);
    }
  }

  async function submitMoveUserRequest(event) {
    event.preventDefault();
    clearRequestFeedback();
    setRequestSubmitting(true);

    try {
      const basePayload = {
        targetEmail: requestTarget.targetEmail,
        targetNombre: requestTarget.targetNombre,
        targetApellido: requestTarget.targetApellido,
        currentState: requestTarget.iglesiaEstado,
        currentCity: requestTarget.iglesiaCiudad,
        currentCountry: requestTarget.pais,
        currentChurchName: requestTarget.iglesiaNombre,
        newState: rowRequestInputs.newState,
        newCity: rowRequestInputs.newCity,
        newChurchName: rowRequestInputs.newChurchName,
        newCountry: rowRequestInputs.newCountry,
      };

      let result;

      try {
        result = await requestMoveUser(basePayload, accessToken);
      } catch (requestError) {
        if (isDuplicateRequestError(requestError.message)) {
          openOverrideDialog('requestMoveUser', basePayload, requestError.message);
          return;
        } else {
          throw requestError;
        }
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud de movimiento guardada.'));
      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }
      setRowRequestInputs((previous) => ({
        ...previous,
        newState: '',
        newCity: '',
        newChurchName: '',
        newCountry: requestTarget.pais,
      }));
    } catch (requestError) {
      setRequestFeedback({
        type: 'error',
        message: toFriendlyRequestMessage(
          requestError.message,
          'No se pudo registrar la solicitud de movimiento.'
        ),
      });
    } finally {
      setRequestSubmitting(false);
    }
  }

  async function submitUpdateUserInfoRequest(event) {
    event.preventDefault();
    clearRequestFeedback();
    setRequestSubmitting(true);

    try {
      const basePayload = {
        targetEmail: requestTarget.targetEmail,
        currentEmail: requestTarget.targetEmail,
        currentNombre: requestTarget.targetNombre,
        currentApellido: requestTarget.targetApellido,
        currentTelefono: requestTarget.telefono,
        currentIdioma: requestTarget.idioma,
        iglesiaEstado: requestTarget.iglesiaEstado,
        iglesiaCiudad: requestTarget.iglesiaCiudad,
        iglesiaNombre: requestTarget.iglesiaNombre,
        pais: requestTarget.pais,
        requestedNombre: normalizeUserRequestInput('Nombre', rowRequestInputs.requestedNombre),
        requestedApellido: normalizeUserRequestInput('Apellido', rowRequestInputs.requestedApellido),
        requestedTelefono: normalizeUserRequestInput('Telefono', rowRequestInputs.requestedTelefono, requestTarget.pais),
        requestedEmail: normalizeUserRequestInput('Email', rowRequestInputs.requestedEmail),
        requestedIdioma: rowRequestInputs.requestedIdioma,
      };

      let result;

      try {
        result = await requestUpdateUserInfo(basePayload, accessToken);
      } catch (requestError) {
        if (isDuplicateRequestError(requestError.message)) {
          openOverrideDialog('requestUpdateUserInfo', basePayload, requestError.message);
          return;
        }

        throw requestError;
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud de datos personales guardada.'));
      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }
      setRowRequestInputs((previous) => ({
        ...previous,
        requestedNombre: basePayload.requestedNombre,
        requestedApellido: basePayload.requestedApellido,
        requestedTelefono: basePayload.requestedTelefono,
        requestedEmail: basePayload.requestedEmail,
        requestedIdioma: rowRequestInputs.requestedIdioma,
      }));
    } catch (requestError) {
      setRequestFeedback({
        type: 'error',
        message: toFriendlyRequestMessage(
          requestError.message,
          'No se pudo registrar la solicitud de datos personales.'
        ),
      });
    } finally {
      setRequestSubmitting(false);
    }
  }

  async function submitDeleteUserRequest() {
    clearRequestFeedback();
    setRequestSubmitting(true);

    try {
      const basePayload = {
        targetEmail: requestTarget.targetEmail,
        targetNombre: requestTarget.targetNombre,
        targetApellido: requestTarget.targetApellido,
        iglesiaEstado: requestTarget.iglesiaEstado,
        iglesiaCiudad: requestTarget.iglesiaCiudad,
        iglesiaNombre: requestTarget.iglesiaNombre,
        pais: requestTarget.pais,
        note: rowRequestInputs.deleteNote,
      };

      let result;

      try {
        result = await requestDeleteUser(basePayload, accessToken);
      } catch (requestError) {
        if (isDuplicateRequestError(requestError.message)) {
          openOverrideDialog('requestDeleteUser', basePayload, requestError.message);
          return;
        } else {
          throw requestError;
        }
      }

      setRequestFeedback(getSavedRequestFeedback(result, 'Solicitud de eliminación guardada.'));
      if (result?.request) {
        onLocationRequestUpsert(result.request);
      }
      setRowRequestInputs((previous) => ({ ...previous, deleteNote: '' }));
    } catch (requestError) {
      setRequestFeedback({
        type: 'error',
        message: toFriendlyRequestMessage(
          requestError.message,
          'No se pudo registrar la solicitud de eliminación.'
        ),
      });
    } finally {
      setRequestSubmitting(false);
    }
  }

  async function submitCreateRowWithOptionalPassword(event) {
    event.preventDefault();

    const targetEmail = emailColumnIndex >= 0 ? String(rowInputs[emailColumnIndex] || '').trim() : '';
    const shouldOpenPasswordDialog = Boolean(
      isUsuariosSheet
      && createPasswordAfterAdd
      && targetEmail
      && editingRowIndex === null
    );

    const success = await submitRow(rowInputsForSubmit);

    if (!success) {
      return;
    }

    if (shouldOpenPasswordDialog) {
      openPasswordDialog(targetEmail);
    }

    setCreatePasswordAfterAdd(false);
  }

  const renderUserInputField = useCallback((value, index, keyPrefix) => {
    const label = headers[index] || `Columna ${index + 1}`;
    const isCreateUsuariosField = keyPrefix === 'create' && isUsuariosSheet;
    const normalizedLabel = normalizeHeaderName(label);
    const isRequiredUsuariosField = isCreateUsuariosField && !OPTIONAL_USUARIOS_FIELD_NAMES.has(normalizedLabel);
    const isMissingRequiredUsuariosField = isRequiredUsuariosField && missingUsuariosRequiredFieldIndexes.has(index);
    const columnLabel = getColumnDisplayName(label, selectedSheet);
    const displayLabel = isRequiredUsuariosField ? `${columnLabel} *` : columnLabel;

    if (isUserPasswordsSheet && normalizedLabel === 'expiraen') {
      const dateInputValue = formatDateTimeInputValue(value);
      const previewValue = formatHumanDateTime(dateInputValue || value, 'Selecciona una fecha y hora');

      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          label={displayLabel}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={dateInputValue}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          disabled={loading}
          color="secondary"
          sx={highlightedDateFieldSx}
          helperText={`Se mostrará como: ${previewValue}`}
        />
      );
    }

    if (isPhoneColumnLabel(label)) {
      return (
        <PhoneTextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          header={label}
          headers={headers}
          row={rowInputs}
          onValueChange={(nextValue) => updateRowDraft(index, nextValue)}
          onValueBlur={(nextValue) => updateRowDraft(index, nextValue, { input: false })}
          error={isMissingRequiredUsuariosField}
          disabled={loading}
        />
      );
    }

    const roleNotificationSetting = isRolesSheet ? getRoleNotificationSetting(label) : null;
    if (roleNotificationSetting) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value || 'No'}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          disabled={loading}
          helperText={roleNotificationSetting.description}
        >
          <MenuItem value="Si">Sí</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      );
    }

    if (isReferenceValueSheet && index === managedValueColumnIndex) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          onBlur={(event) => updateRowDraft(index, event.target.value, { input: false })}
          error={isMissingRequiredUsuariosField}
          disabled={loading}
        />
      );
    }

    if (index === laborRoleColumnIndex) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading || rolesLoading}
        >
          <MenuItem value="">
            <em>Seleccionar rol</em>
          </MenuItem>
          {roleOptions.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (index === creadoColumnIndex) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Seleccionar estado</em>
          </MenuItem>
          <MenuItem value="Si">Si</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      );
    }

    if (iglesiaEstadoInputIndexes.includes(index)) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Seleccionar estado</em>
          </MenuItem>
          {locationSuggestions.states.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (iglesiaCiudadInputIndexes.includes(index)) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Seleccionar ciudad</em>
          </MenuItem>
          {locationSuggestions.cities.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (iglesiaNombreInputIndexes.includes(index)) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Sin iglesia específica</em>
          </MenuItem>
          {locationSuggestions.churchNames.map((churchName) => (
            <MenuItem key={churchName} value={churchName}>
              {churchName}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (paisInputIndexes.includes(index)) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Seleccionar país</em>
          </MenuItem>
          {locationSuggestions.countries.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (idiomaInputIndexes.includes(index)) {
      return (
        <TextField
          key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
          select
          sx={adminDropdownSx}
          label={displayLabel}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => updateRowDraft(index, event.target.value)}
          error={isMissingRequiredUsuariosField}
          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Sin idioma</em>
          </MenuItem>
          {locationSuggestions.languages.map((language) => (
            <MenuItem key={language} value={language}>
              {language}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        key={`${keyPrefix}-${index}-${headers[index] || 'field'}`}
        label={displayLabel}
        InputLabelProps={{ shrink: true }}
        value={value}
        onChange={(event) => updateRowDraft(index, event.target.value)}
        onBlur={(event) => updateRowDraft(index, event.target.value, { input: false })}
        error={isMissingRequiredUsuariosField}
      />
    );
  }, [
    adminDropdownMenuProps,
    adminDropdownSx,
    creadoColumnIndex,
    headers,
    idiomaInputIndexes,
    iglesiaCiudadInputIndexes,
    iglesiaNombreInputIndexes,
    iglesiaEstadoInputIndexes,
    isUsuariosSheet,
    isReferenceValueSheet,
    isRolesSheet,
    isUserPasswordsSheet,
    laborRoleColumnIndex,
    loading,
    locationSuggestions.churchNames,
    locationSuggestions.cities,
    locationSuggestions.countries,
    locationSuggestions.languages,
    locationSuggestions.states,
    managedValueColumnIndex,
    missingUsuariosRequiredFieldIndexes,
    paisInputIndexes,
    highlightedDateFieldSx,
    roleOptions,
    rolesLoading,
    rowInputs,
    selectedSheet,
    updateRowDraft,
  ]);

  const renderUsuariosLocationEditor = useCallback((keyPrefix) => (
    <Stack spacing={1.25} sx={{ gridColumn: '1 / -1' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2">Sedes</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Cada bloque representa una sede y las cuatro columnas se guardan por índice.
          </Typography>
        </Box>
        <Button type="button" variant="outlined" onClick={addLocationDraft} disabled={loading} color='secondary'>
          + Agregar otra locacion
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.25,
          minWidth: 0,
        }}
      >
        {rowInputLocations.map((location, locationIndex) => {
          const locationErrors = rowInputLocationErrors[locationIndex] || {};

          return (
            <Paper key={`${keyPrefix}-location-${locationIndex}`} variant="outlined" sx={{ p: 1.25, minWidth: 0, overflow: 'hidden' }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2">Locación {locationIndex + 1}</Typography>
                  <Button
                    type="button"
                    variant="text"
                    color="inherit"
                    disabled={loading || rowInputLocations.length === 1}
                    onClick={() => removeLocationDraft(locationIndex)}
                  >
                    Quitar
                  </Button>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    minWidth: 0,
                    '& > *': {
                      minWidth: 0,
                    },
                  }}
                >
                  <TextField
                    select
                    sx={adminDropdownSx}
                    label={`${getColumnDisplayName(LOCATION_HEADERS.state, 'Usuarios')} *`}
                    InputLabelProps={{ shrink: true }}
                    value={location.state}
                    onChange={(event) => updateLocationDraft(locationIndex, 'state', event.target.value)}
                    error={locationErrors.state}
                    SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Seleccionar estado</em>
                    </MenuItem>
                    {locationSuggestions.states.map((state) => (
                      <MenuItem key={`location-state-${locationIndex}-${state}`} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    sx={adminDropdownSx}
                    label={`${getColumnDisplayName(LOCATION_HEADERS.city, 'Usuarios')} *`}
                    InputLabelProps={{ shrink: true }}
                    value={location.city}
                    onChange={(event) => updateLocationDraft(locationIndex, 'city', event.target.value)}
                    error={locationErrors.city}
                    SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Seleccionar ciudad</em>
                    </MenuItem>
                    {locationSuggestions.cities.map((city) => (
                      <MenuItem key={`location-city-${locationIndex}-${city}`} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    sx={adminDropdownSx}
                    label={getColumnDisplayName(LOCATION_HEADERS.churchName, 'Usuarios')}
                    InputLabelProps={{ shrink: true }}
                    value={location.churchName}
                    onChange={(event) => updateLocationDraft(locationIndex, 'churchName', event.target.value)}
                    SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Sin iglesia específica</em>
                    </MenuItem>
                    {locationSuggestions.churchNames.map((churchName) => (
                      <MenuItem key={`location-church-${locationIndex}-${churchName}`} value={churchName}>
                        {churchName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    sx={adminDropdownSx}
                    label={`${getColumnDisplayName(LOCATION_HEADERS.country, 'Usuarios')} *`}
                    InputLabelProps={{ shrink: true }}
                    value={location.country}
                    onChange={(event) => updateLocationDraft(locationIndex, 'country', event.target.value)}
                    error={locationErrors.country}
                    SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Seleccionar país</em>
                    </MenuItem>
                    {locationSuggestions.countries.map((country) => (
                      <MenuItem key={`location-country-${locationIndex}-${country}`} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Stack>
  ), [
    addLocationDraft,
    adminDropdownMenuProps,
    adminDropdownSx,
    loading,
    locationSuggestions.churchNames,
    locationSuggestions.cities,
    locationSuggestions.countries,
    locationSuggestions.states,
    removeLocationDraft,
    rowInputLocationErrors,
    rowInputLocations,
    updateLocationDraft,
  ]);

  const createLocationEditor = useMemo(() => renderUsuariosLocationEditor('create'), [renderUsuariosLocationEditor]);
  const editLocationEditor = useMemo(() => renderUsuariosLocationEditor('edit'), [renderUsuariosLocationEditor]);

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        p: { xs: 1, md: 1 },
      }}
    >
      <Stack spacing={1.75}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack spacing={1.75}>
            {isMainAdminUser && (
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <FormControl sx={{ minWidth: 190 }} disabled={sheets.length === 0 || loading}>
                    <Select
                      value={selectedSheet}
                      sx={adminDropdownSx}
                      MenuProps={adminDropdownMenuProps}
                      displayEmpty
                      renderValue={(value) => {
                        const currentValue = String(value || '').trim();
                        if (!currentValue) {
                          return <em>Seleccionar hoja</em>;
                        }

                        return renderSheetOptionLabel(currentValue);
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSelectedSheet(value);
                        loadSheetData(value);
                      }}
                    >
                      <MenuItem value="">
                        <em>Seleccionar hoja</em>
                      </MenuItem>
                      {sheets.map((sheet) => (
                        <MenuItem key={sheet.sheetId || sheet.title} value={sheet.title}>
                          {renderSheetOptionLabel(sheet.title)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Actualizar datos" arrow>
                    <span>
                      <IconButton
                        aria-label="Actualizar datos"
                        disabled={!selectedSheet || loading || refreshing || hasUnsavedDraft}
                        onClick={() => {
                          loadSheetData(selectedSheet, { force: true });
                          locationStatusState.refresh();
                        }}
                        size="small"
                      >
                        {refreshing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Ver solicitudes pendientes por hoja" arrow>
                      <Button
                        type="button"
                        variant="text"
                        onClick={openPendingSheetsDialog}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          textTransform: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip label={`Solicitudes pendientes`} color="secondary" size="small" />
                        <Chip label={totalPendingRequests} color="error" size="small" />
                      </Button>
                    </Tooltip>
                  </Box>
                </Stack>
              </Stack>
            )}

            {isReadOnlyUser && isUsuariosSheet && (
              <LocationStatusPanel
                accessToken={accessToken}
                statuses={locationStatusState.statuses}
                loading={locationStatusState.loading}
                error={locationStatusState.error}
                onRefresh={locationStatusState.refresh}
              />
            )}

            {isMainAdminUser && isUsuariosSheet && locationStatusState.error && (
              <Alert severity="warning" action={<Button color="inherit" onClick={locationStatusState.refresh}>Reintentar</Button>}>
                No se pudo consultar el estado de las sedes. {locationStatusState.error}
              </Alert>
            )}

            {isMainAdminUser && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                <Stack spacing={1.25}>
                  {isRolesSheet && (
                    <Alert severity="info">
                      <Stack spacing={0.5}>
                        <Typography variant="subtitle2">Notificaciones por rol</Typography>
                        {ROLE_NOTIFICATION_SETTINGS.map((setting) => (
                          <Typography key={setting.header} variant="body2">
                            <strong>{setting.label}:</strong> {setting.description}
                          </Typography>
                        ))}
                        <Typography variant="body2">
                          Por ejemplo, activa Solicitudes en mi sede para el rol Pastor. Los pastores recibirán el resumen de solicitudes pendientes cuando se pulse TERMINAR Y ENVIAR.
                        </Typography>
                      </Stack>
                    </Alert>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {createPanelTitle}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary' }}>
                        Mantenga este panel cerrado para enfocarse en la tabla y ábralo solo cuando necesite crear o completar datos.
                      </Typography>
                    </Stack>
                    <Button
                      type="button"
                      variant={isCreatePanelOpen ? 'outlined' : 'contained'}
                      startIcon={<PlusSquare size={16} />}
                      endIcon={isCreatePanelOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      disabled={!selectedSheet}
                      onClick={() => setIsCreatePanelOpen((previous) => !previous)}
                    >
                      {isCreatePanelOpen ? 'Ocultar formulario' : 'Mostrar formulario'}
                    </Button>
                  </Box>

                  <Collapse in={isCreatePanelOpen} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box component="form" onSubmit={submitCreateRowWithOptionalPassword}>
                        {isUsuariosSheet && headers.length > 0 && (
                          <Stack spacing={1.25} sx={{ mb: 1.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Los campos obligatorios se marcan con *.
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {missingUsuariosRequiredFields.length > 0 ? (
                                missingUsuariosRequiredFields.map((field) => (
                                  <Chip
                                    key={`missing-required-${field.index}`}
                                    label={field.label}
                                    color="warning"
                                    variant="filled"
                                    size="small"
                                  />
                                ))
                              ) : (
                                <Chip
                                  label="Todos los campos obligatorios están completos"
                                  color="success"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </Box>
                          </Stack>
                        )}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 1fr))', xl: 'repeat(4, minmax(180px, 1fr))' },
                            gap: 1.25,
                            alignItems: 'start',
                          }}
                        >
                          {isUsuariosSheet && createLocationEditor}
                          {rowInputs.map((value, index) => (
                            shouldHideUsuariosFieldFromUi(index)
                              ? null
                              : renderUserInputField(value, index, 'create')
                          ))}
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ gridColumn: { xs: 'auto', md: '1 / -1' }, pt: 0.5 }}
                          >
                            {isUsuariosSheet && emailColumnIndex >= 0 ? (
                              <FormControlLabel
                                control={(
                                  <Checkbox
                                    checked={createPasswordAfterAdd}
                                    onChange={(event) => setCreatePasswordAfterAdd(event.target.checked)}
                                    disabled={loading || editingRowIndex !== null}
                                  />
                                )}
                                label="Crear contraseña después de agregar"
                                sx={{ mr: 0 }}
                              />
                            ) : (
                              <Box />
                            )}
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={!selectedSheet || loading || refreshing || editingRowIndex !== null || !canSubmitUsuariosRow}
                              sx={{ minWidth: 160, alignSelf: { xs: 'stretch', sm: 'center' } }}
                            >
                              {selectedSheet && headers.length === 0 ? 'Crear columnas' : 'Agregar datos'}
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Stack>
              </Box>
            )}

            {isReadOnlyUser && isUsuariosSheet && (
              <Box sx={{ pt: 0.5 }}>
                <Stack spacing={1.25}>
                  {authLocationScopes.length > 1 && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Ver usuarios por sede</Typography>
                        <Tabs
                          value={activeLocationTab}
                          onChange={(_, nextValue) => setActiveLocationTab(nextValue)}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                            sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 700, px: { xs: 1, sm: 1.5 } } }}
                        >
                          {authLocationScopes.length > 1 && <Tab value="all" label="Mostrar todas" />}
                          {authLocationScopes.map((location, index) => (
                            <Tab
                              key={`scope-tab-${index}`}
                              value={`scope-${index}`}
                                label={isMobileViewport ? `Sede ${index + 1}` : `Sede ${index + 1}: ${formatLocationScopeLabel(location)}`}
                            />
                          ))}
                        </Tabs>
                        {authLocationScopes.length > 1 && activeLocationTab === 'all' && (
                          <Alert severity="info">
                            Estás viendo todas tus sedes. Para crear solicitudes nuevas usa una pestaña de sede específica.
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}
                  <Paper variant="outlined" sx={{ p: 1.25 }}>
                    <Stack spacing={1}>
                      <Button
                        type="button"
                        variant={isNewUserRequestOpen ? 'outlined' : 'contained'}
                        onClick={() => setIsNewUserRequestOpen((previous) => !previous)}
                        endIcon={isNewUserRequestOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                      >
                        Solicitar nuevo usuario
                      </Button>
                      <Collapse in={isNewUserRequestOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={1.25}>
                          {newUserRequestScope && (
                            <Typography sx={{ color: 'text.secondary' }}>
                              Nueva solicitud para: {formatLocationScopeLabel(newUserRequestScope)}
                            </Typography>
                          )}
                          <Box component="form" onSubmit={submitNewUserRequest}>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                gap: 1,
                                alignItems: 'center',
                              }}
                            >
                              <TextField
                                label="Nombre"
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.nombre}
                                onChange={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  nombre: normalizeUserRequestInput('Nombre', event.target.value, '', true),
                                }))}
                                onBlur={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  nombre: normalizeUserRequestInput('Nombre', event.target.value),
                                }))}
                                disabled={requestSubmitting}
                              />
                              <TextField
                                label="Apellido"
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.apellido}
                                onChange={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  apellido: normalizeUserRequestInput('Apellido', event.target.value, '', true),
                                }))}
                                onBlur={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  apellido: normalizeUserRequestInput('Apellido', event.target.value),
                                }))}
                                disabled={requestSubmitting}
                              />
                              <TextField
                                label="Email"
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.email}
                                onChange={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  email: normalizeUserRequestInput('Email', event.target.value, '', true),
                                }))}
                                onBlur={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  email: normalizeUserRequestInput('Email', event.target.value),
                                }))}
                                disabled={requestSubmitting}
                              />
                              <PhoneTextField
                                label="Telefono"
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.telefono}
                                countryValue={newUserRequestScope?.country}
                                onValueChange={(nextValue) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  telefono: normalizeUserRequestInput('Telefono', nextValue, newUserRequestScope?.country, true),
                                }))}
                                onValueBlur={(nextValue) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  telefono: normalizeUserRequestInput('Telefono', nextValue, newUserRequestScope?.country),
                                }))}
                                disabled={requestSubmitting}
                              />
                              <TextField
                                label="Labor/Rol"
                                select
                                sx={adminDropdownSx}
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.laborRol}
                                onChange={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  laborRol: event.target.value,
                                }))}
                                SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                                disabled={requestSubmitting || rolesLoading}
                              >
                                <MenuItem value="">
                                  <em>Seleccionar rol</em>
                                </MenuItem>
                                {requestRoleOptions.map((role) => (
                                  <MenuItem key={role} value={role}>
                                    {role}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                label="Idioma"
                                select
                                sx={adminDropdownSx}
                                InputLabelProps={{ shrink: true }}
                                value={newUserRequestInputs.idioma}
                                onChange={(event) => setNewUserRequestInputs((previous) => ({
                                  ...previous,
                                  idioma: event.target.value,
                                }))}
                                SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                                disabled={requestSubmitting}
                              >
                                <MenuItem value="">
                                  <em>Sin idioma</em>
                                </MenuItem>
                                {locationSuggestions.languages.map((language) => (
                                  <MenuItem key={language} value={language}>
                                    {language}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Button
                                type="submit"
                                variant="contained"
                                disabled={requestSubmitting || !canSubmitNewUserRequest}
                                sx={{ minWidth: 110, width: { xs: '100%', sm: 'auto' } }}
                              >
                                {requestSubmitting ? 'Guardando...' : 'Guardar solicitud'}
                              </Button>
                            </Box>
                          </Box>
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            )}

            <Box>
              {isMainAdminUser && isSelectedRequestSheet && (
                <Alert
                  severity="info"
                  icon={<Info size={22} />}
                  sx={{ mb: 1.25, alignItems: 'center' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Registro de cambios externos
                  </Typography>
                  <Typography variant="body2">
                    Esta aplicación solo registra y mantiene actualizada la información. No ejecuta cambios en aplicaciones o sistemas externos. Usa <strong>Ejecutado</strong> únicamente después de completar el cambio correspondiente fuera de esta aplicación.
                  </Typography>
                </Alert>
              )}
              {isMainAdminUser && quickHideLocationColumnFieldKeys.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={hideLocationColumnsQuick}
                        onChange={(event) => setHideLocationColumnsQuick(event.target.checked)}
                      />
                    )}
                    label={`Ocultar columnas de sede (${[
                      COUNTRY_SHEET_NAME,
                      CHURCH_STATE_SHEET_NAME,
                      CHURCH_CITY_SHEET_NAME,
                      CHURCH_NAME_SHEET_NAME,
                    ].map((header) => getColumnDisplayName(header, selectedSheet)).join(', ')})`}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                  />
                </Paper>
              )}
              {isUsuariosSheet && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start" sx={{ mb: 1, '& > .MuiFormControl-root': { mb: 0, width: { xs: '100%', sm: 'auto' } } }}>
                <LocationStatusQuickFilter
                  value={activeLocationStatusFilter?.value || ''}
                  onChange={applyLocationStatusQuickFilter}
                  loading={locationStatusState.loading}
                />
                <TextField
                  type="search"
                  label="Buscar usuarios"
                  placeholder="Nombre, email, teléfono…"
                  value={globalSearch}
                  onChange={(event) => changeGlobalSearch(event.target.value)}
                  sx={{ flex: 1, maxWidth: { sm: 520 }, '& input::-webkit-search-cancel-button': { WebkitAppearance: 'none' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Search size={18} aria-hidden="true" /></InputAdornment>,
                    endAdornment: globalSearch && <InputAdornment position="end"><IconButton aria-label="Limpiar búsqueda" onClick={() => changeGlobalSearch('')} edge="end"><X size={16} /></IconButton></InputAdornment>,
                  }}
                />
                </Stack>
              )}
              {(activeGridFilterItems.length > 0 || globalSearchTerms.length > 0) && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  {globalSearchTerms.length > 0 && <Chip label={`Buscar: ${globalSearch.trim()}`} onDelete={() => changeGlobalSearch('')} variant="outlined" color="secondary" />}
                  {activeGridFilterItems.map((filterItem) => (
                    <Chip
                      key={filterItem.field}
                      label={formatGridFilterChipLabel(filterItem, headers, selectedSheet)}
                      onDelete={() => clearGridFilterForField(filterItem.field)}
                      variant="outlined"
                      color='secondary'
                    />
                  ))}
                  <Button type="button" size="small" variant="text" onClick={clearAllGridFilters}>
                    Limpiar filtros
                  </Button>
                </Stack>
              )}
              <SheetsGrid
                hasData={!(headers.length === 0 && rows.length === 0)}
                gridRows={gridRows}
                gridColumns={gridColumns}
                loading={loading || (Boolean(activeLocationStatusFilter) && locationStatusState.loading)}
                gridInitialState={gridInitialState}
                paginationModel={gridPaginationModel}
                onPaginationModelChange={setGridPaginationModel}
                filterModel={gridFilterModel}
                onFilterModelChange={handleGridFilterModelChange}
                columnVisibilityModel={effectiveGridColumnVisibilityModel}
                onColumnVisibilityModelChange={setGridColumnVisibilityModel}
                isMobileViewport={isMobileViewport}
                useAutoRowHeight={isMobileViewport && isSelectedRequestSheet && !isReadOnlyUser}
              />
            </Box>
          </Stack>
        </Paper>

        {loading && <Alert severity="info">Cargando...</Alert>}
      </Stack>

      <Snackbar
        open={errorToastOpen}
        autoHideDuration={7000}
        onClose={handleCloseErrorToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={handleCloseErrorToast}
          sx={{
            width: '100%',
            minWidth: { xs: 280, sm: 420 },
            fontWeight: 500,
            boxShadow: 6,
          }}
        >
          {formatSheetDisplayText(formatDateTimesInText(error))}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(rolesError)}
        autoHideDuration={7000}
        onClose={handleCloseRolesErrorToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={handleCloseRolesErrorToast}
          sx={{
            width: '100%',
            minWidth: { xs: 280, sm: 420 },
            fontWeight: 500,
            boxShadow: 6,
          }}
        >
          {formatDateTimesInText(rolesError)}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(requestFeedback.message)}
        autoHideDuration={7000}
        onClose={handleCloseRequestFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={requestFeedback.type === 'warning' ? 'warning' : (requestFeedback.type === 'success' ? 'success' : 'error')}
          variant="filled"
          onClose={handleCloseRequestFeedback}
          sx={{
            width: '100%',
            minWidth: { xs: 280, sm: 420 },
            fontWeight: 500,
            boxShadow: 6,
          }}
        >
          {formatSheetDisplayText(formatDateTimesInText(requestFeedback.message))}
        </Alert>
      </Snackbar>

      {isMainAdminUser && (
        <Dialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              width: '95vw',
              maxWidth: 1500,
            },
          }}
        >
          <DialogTitle>
            {editingRowIndex === null ? 'Editar fila' : `Editar fila #${editingRowIndex + 1}`}
          </DialogTitle>
          <DialogContent dividers>
            <Box
              component="form"
              id="edit-row-form"
              onSubmit={submitEditDialog}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 1fr))', xl: 'repeat(4, minmax(180px, 1fr))' },
                gap: 1,
                mt: 0.5,
              }}
            >
              {isUsuariosSheet && editLocationEditor}
              {rowInputs.map((value, index) => (
                shouldHideUsuariosFieldFromUi(index)
                  ? null
                  : renderUserInputField(value, index, 'edit')
              ))}
              {isUsuariosSheet && (
                <AdminLocationStatusEditor
                  locations={rowInputLocations}
                  statuses={userLocationStatusIndex.get(userReviewEmailKey(rowInputs[emailColumnIndex]))?.statuses || []}
                  drafts={editStatusDrafts}
                  onChange={(scope, status) => setEditStatusDrafts((previous) => ({ ...previous, [locationStatusKey(scope)]: status }))}
                  disabled={loading}
                  loading={locationStatusState.loading}
                  error={locationStatusState.error}
                  description="La revisión se aplica solo a este usuario en cada sede. Los cambios se guardan al pulsar Guardar cambios."
                  confirmationHelperText="La revisión se guarda solo para este usuario."
                />
              )}
              {editStatusFeedback && <Alert severity={editStatusFeedback.type} sx={{ gridColumn: '1 / -1' }}>{editStatusFeedback.message}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant="text" onClick={closeEditDialog} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" type="submit" form="edit-row-form" disabled={loading || refreshing || !canSubmitUsuariosRow}>
              Guardar cambios
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={duplicateUserDialog.open}
        onClose={() => {
          if (!duplicateUserDialog.submitting) {
            setDuplicateUserDialog({ open: false, conflict: null, submitting: false });
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Resolver usuario duplicado</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El email <strong>{duplicateUserDialog.conflict?.duplicatedValue || ''}</strong> está repetido.
            Compara los datos y selecciona el usuario correcto. Los demás registros con este email se eliminarán.
          </Alert>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {renderDuplicateUserCard({
              row: duplicateUserDialog.conflict?.currentRow,
              rowIndex: duplicateUserDialog.conflict?.currentRowIndex,
              kind: 'edited',
            })}
            {renderDuplicateUserCard({
              row: duplicateUserDialog.conflict?.duplicateRow,
              rowIndex: duplicateUserDialog.conflict?.duplicateRowIndex,
              kind: 'existing',
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateUserDialog({ open: false, conflict: null, submitting: false })}
            disabled={duplicateUserDialog.submitting}
          >
            Volver sin eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {isReadOnlyUser && (
        <Dialog
          open={requestDialog.open}
          onClose={closeRequestDialog}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobileViewport}
        >
          <DialogTitle>Solicitudes para usuario</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Usuario: {requestTarget.targetNombre} {requestTarget.targetApellido} ({requestTarget.targetEmail})
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Sede seleccionada: {formatLocationScopeLabel(selectedRequestLocation, { emptyLabel: 'Sin sede asignada' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Guarda los cambios. Al finalizar, pulsa <strong>TERMINAR Y ENVIAR</strong>.
              </Typography>

              <UserDataReview
                key={`${requestTarget.targetEmail}:${locationStatusKey(selectedRequestLocation)}`}
                targetEmail={requestTarget.targetEmail}
                locationScope={selectedRequestLocation}
                statuses={userLocationStatusIndex.get(userReviewEmailKey(requestTarget.targetEmail))?.statuses || []}
                loading={locationStatusState.loading}
                error={locationStatusState.error}
                accessToken={accessToken}
                onRefresh={locationStatusState.refresh}
                disabled={requestSubmitting}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: 1.5,
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => toggleRequestSection(REQUEST_DIALOG_SECTIONS.ROLE_CHANGE)}
                    sx={{ width: '100%', px: 0, py: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="subtitle2">Solicitar cambio de rol</Typography>
                      {expandedRequestSection === REQUEST_DIALOG_SECTIONS.ROLE_CHANGE ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </Box>
                  </Button>
                  <Collapse in={expandedRequestSection === REQUEST_DIALOG_SECTIONS.ROLE_CHANGE} timeout="auto" unmountOnExit>
                    <Box component="form" onSubmit={submitRoleChangeRequest} sx={{ mt: 1 }}>
                      <Stack spacing={1}>
                        <TextField
                          label="Rol actual"
                          InputLabelProps={{ shrink: true }}
                          value={requestTarget.currentRole}
                          disabled
                        />
                        <TextField
                          label="Rol solicitado"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedRole}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedRole: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Seleccionar rol</em>
                          </MenuItem>
                          {requestRoleOptions.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Box>
                          <Button type="submit" variant="contained" disabled={requestSectionDisabled}>
                            {requestSubmitting ? 'Guardando...' : 'Guardar cambios'}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => toggleRequestSection(REQUEST_DIALOG_SECTIONS.MOVE_USER)}
                    sx={{ width: '100%', px: 0, py: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="subtitle2">Solicitar mover usuario</Typography>
                      {expandedRequestSection === REQUEST_DIALOG_SECTIONS.MOVE_USER ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </Box>
                  </Button>
                  <Collapse in={expandedRequestSection === REQUEST_DIALOG_SECTIONS.MOVE_USER} timeout="auto" unmountOnExit>
                    <Box component="form" onSubmit={submitMoveUserRequest} sx={{ mt: 1 }}>
                      <Stack spacing={1}>
                        <TextField
                          label="Iglesia - Estado nuevo"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.newState}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            newState: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Seleccionar estado</em>
                          </MenuItem>
                          {locationSuggestions.states.map((state) => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Iglesia - Ciudad nueva"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.newCity}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            newCity: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Seleccionar ciudad</em>
                          </MenuItem>
                          {locationSuggestions.cities.map((city) => (
                            <MenuItem key={city} value={city}>
                              {city}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Iglesia - Nombre nuevo"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.newChurchName}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            newChurchName: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Sin iglesia específica</em>
                          </MenuItem>
                          {locationSuggestions.churchNames.map((churchName) => (
                            <MenuItem key={churchName} value={churchName}>
                              {churchName}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="País nuevo"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.newCountry}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            newCountry: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Seleccionar país</em>
                          </MenuItem>
                          {locationSuggestions.countries.map((country) => (
                            <MenuItem key={country} value={country}>
                              {country}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Box>
                          <Button type="submit" variant="contained" disabled={requestSectionDisabled}>
                            {requestSubmitting ? 'Guardando...' : 'Guardar cambios'}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => toggleRequestSection(REQUEST_DIALOG_SECTIONS.UPDATE_USER_INFO)}
                    sx={{ width: '100%', px: 0, py: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="subtitle2">Solicitar actualización de datos personales</Typography>
                      {expandedRequestSection === REQUEST_DIALOG_SECTIONS.UPDATE_USER_INFO ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </Box>
                  </Button>
                  <Collapse in={expandedRequestSection === REQUEST_DIALOG_SECTIONS.UPDATE_USER_INFO} timeout="auto" unmountOnExit>
                    <Box component="form" onSubmit={submitUpdateUserInfoRequest} sx={{ mt: 1 }}>
                      <Stack spacing={1}>
                        <TextField
                          label="Nombre solicitado"
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedNombre}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedNombre: normalizeUserRequestInput('Nombre', event.target.value, '', true),
                          }))}
                          onBlur={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedNombre: normalizeUserRequestInput('Nombre', event.target.value),
                          }))}
                          disabled={requestSectionDisabled}
                        />
                        <TextField
                          label="Apellido solicitado"
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedApellido}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedApellido: normalizeUserRequestInput('Apellido', event.target.value, '', true),
                          }))}
                          onBlur={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedApellido: normalizeUserRequestInput('Apellido', event.target.value),
                          }))}
                          disabled={requestSectionDisabled}
                        />
                        <PhoneTextField
                          label="Telefono solicitado"
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedTelefono}
                          countryValue={requestTarget.pais}
                          onValueChange={(nextValue) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedTelefono: normalizeUserRequestInput('Telefono', nextValue, requestTarget.pais, true),
                          }))}
                          onValueBlur={(nextValue) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedTelefono: normalizeUserRequestInput('Telefono', nextValue, requestTarget.pais),
                          }))}
                          disabled={requestSectionDisabled}
                        />
                        <TextField
                          label="Email solicitado"
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedEmail}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedEmail: normalizeUserRequestInput('Email', event.target.value, '', true),
                          }))}
                          onBlur={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedEmail: normalizeUserRequestInput('Email', event.target.value),
                          }))}
                          disabled={requestSectionDisabled}
                        />
                        <TextField
                          label="Idioma solicitado"
                          select
                          sx={adminDropdownSx}
                          InputLabelProps={{ shrink: true }}
                          value={rowRequestInputs.requestedIdioma}
                          onChange={(event) => setRowRequestInputs((previous) => ({
                            ...previous,
                            requestedIdioma: event.target.value,
                          }))}
                          SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                          disabled={requestSectionDisabled}
                        >
                          <MenuItem value="">
                            <em>Sin idioma</em>
                          </MenuItem>
                          {locationSuggestions.languages.map((language) => (
                            <MenuItem key={language} value={language}>
                              {language}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Box>
                          <Button type="submit" variant="contained" disabled={requestSectionDisabled}>
                            {requestSubmitting ? 'Guardando...' : 'Guardar cambios'}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => toggleRequestSection(REQUEST_DIALOG_SECTIONS.DELETE_USER)}
                    sx={{ width: '100%', px: 0, py: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="subtitle2">Solicitar eliminar usuario</Typography>
                      {expandedRequestSection === REQUEST_DIALOG_SECTIONS.DELETE_USER ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </Box>
                  </Button>
                  <Collapse in={expandedRequestSection === REQUEST_DIALOG_SECTIONS.DELETE_USER} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <TextField
                        label="Nota"
                        InputLabelProps={{ shrink: true }}
                        value={rowRequestInputs.deleteNote}
                        onChange={(event) => setRowRequestInputs((previous) => ({
                          ...previous,
                          deleteNote: event.target.value,
                        }))}
                        disabled={requestSectionDisabled}
                        multiline
                        minRows={3}
                      />
                      <Box>
                        <Button
                          type="button"
                          color="error"
                          variant="contained"
                          disabled={requestSectionDisabled}
                          onClick={submitDeleteUserRequest}
                        >
                          {requestSubmitting ? 'Guardando...' : 'Guardar cambios'}
                        </Button>
                      </Box>
                    </Stack>
                  </Collapse>
                </Paper>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
            <Button type="button" variant="text" onClick={closeRequestDialog}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={requesterInfoDialog.open}
        onClose={closeRequesterInfoDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: '94vw', maxWidth: 1380 } }}
      >
        <DialogTitle>Detalle de la solicitud</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              mt: 0.5,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.8fr) minmax(240px, 0.9fr) minmax(440px, 1.5fr)' },
              gap: 2,
              alignItems: 'start',
              overflowX: 'hidden',
            }}
          >
            <Paper variant="outlined" sx={{ minWidth: 0, p: 1.5 }}>
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">Solicitante</Typography>
                {requesterInfoEntries.length === 0 ? (
                  <Alert severity="info">No hay datos del solicitante disponibles.</Alert>
                ) : (
                  requesterInfoEntries.map((entry) => (
                    <Box key={`requester-${entry.label}`}>
                      <Typography variant="subtitle2">{getColumnDisplayName(entry.label)}</Typography>
                      <Typography sx={{ color: 'text.secondary', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                        {entry.value}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ minWidth: 0, p: 1.5 }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Usuario afectado</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Persona para quien se solicitó el cambio
                  </Typography>
                </Box>
                {requestTargetEntries.length === 0 ? (
                  <Alert severity="info">No hay datos del usuario disponibles.</Alert>
                ) : (
                  requestTargetEntries.map((entry) => (
                    <Box key={`target-${entry.label}`}>
                      <Typography variant="subtitle2">{getColumnDisplayName(entry.label)}</Typography>
                      <Typography sx={{ color: 'text.secondary', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                        {entry.value}
                      </Typography>
                    </Box>
                  ))
                )}
              </Stack>
            </Paper>

            <RequestChangeComparison entries={requestChangeEntries} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Button
            type="button"
            variant="contained"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={dismissRequestFromDetails}
            disabled={Boolean(requestRowAction.mode)}
          >
            Descartar
          </Button>
          <Tooltip
            title="Marca la solicitud como ejecutada externamente y actualiza el registro interno. Esta aplicación no realiza cambios en sistemas externos."
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button
                type="button"
                variant="contained"
                startIcon={<CheckCircle2 size={16} />}
                onClick={executeRequestFromDetails}
                disabled={Boolean(requestRowAction.mode)}
              >
                Ejecutado
              </Button>
            </Box>
          </Tooltip>
          <Button type="button" variant="text" onClick={closeRequesterInfoDialog} disabled={Boolean(requestRowAction.mode)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {(isReadOnlyUser || isMainAdminUser) && (
        <Dialog
          open={pendingRequestsDialog.open}
          onClose={closePendingRequestsDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Solicitudes pendientes del usuario</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Usuario: {pendingRequestsDialog.email || '-'}
              </Typography>
              <PendingRequestsOverview
                requests={activePendingRequests}
                title=""
                onCancelRequest={isReadOnlyUser ? onCancelRequest : undefined}
                cancelling={cancellingRequests}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant="text" onClick={closePendingRequestsDialog}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isMainAdminUser && (
        <Dialog
          open={pendingSheetsDialogOpen}
          onClose={closePendingSheetsDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Solicitudes pendientes por hoja</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              {pendingRequestSheetItems.map((item) => (
                <Paper key={item.name} variant="outlined" sx={{ p: 1 }}>
                  <Button
                    type="button"
                    variant="text"
                    disabled={!item.available}
                    onClick={() => openRequestSheetFromPendingDialog(item.name)}
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      px: 1,
                    }}
                  >
                    <Typography>{item.name}</Typography>
                    <Chip
                      label={item.pendingCount}
                      color={item.pendingCount > 0 ? 'error' : 'primary'}
                      size="small"
                    />
                  </Button>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant="text" onClick={closePendingSheetsDialog}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isMainAdminUser && (
        <Dialog
          open={locationDetailsDialog.open}
          onClose={() => setLocationDetailsDialog({ open: false, rowIndex: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Sedes del usuario</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              {locationDetailsRows.length === 0 ? (
                <Alert severity="info">No hay sedes disponibles para este usuario.</Alert>
              ) : (
                locationDetailsRows.map((location, index) => (
                  <Paper key={`location-detail-${index}`} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">Locación {index + 1}</Typography>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {formatLocationScopeLabel(location)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" variant="text" onClick={() => setLocationDetailsDialog({ open: false, rowIndex: null })}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isMainAdminUser && (
        <Dialog
          open={passwordDialog.open}
          onClose={closePasswordDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Gestión de contraseñas de usuarios</DialogTitle>
          <DialogContent dividers>
            <UserPasswordsAdmin
              accessToken={accessToken}
              initialEmail={passwordDialog.email}
              initialLocationLabel={passwordDialog.locationLabel}
              onBusyChange={setPasswordDialogBusy}
              onCompleted={handlePasswordCompleted}
              embedded
            />
          </DialogContent>
          <DialogActions>
            <Button type="button" variant="text" onClick={closePasswordDialog} disabled={passwordDialogBusy}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={createSheetDialogOpen}
        onClose={() => setCreateSheetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar hoja</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="create-sheet-form" onSubmit={submitCreateSheetDialog}>
            <TextField
              fullWidth
              label="Nombre de hoja"
              InputLabelProps={{ shrink: true }}
              value={createSheetName}
              onChange={(event) => setCreateSheetName(event.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setCreateSheetDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="create-sheet-form"
            disabled={!canCreateSheet || loading}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameSheetDialogOpen}
        onClose={() => setRenameSheetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renombrar hoja</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="rename-sheet-form" onSubmit={submitRenameSheetDialog}>
            <TextField
              fullWidth
              label="Nuevo nombre de hoja"
              InputLabelProps={{ shrink: true }}
              value={newSheetName}
              onChange={(event) => setNewSheetName(event.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setRenameSheetDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="rename-sheet-form"
            disabled={!canRenameSheet || loading}
          >
            Renombrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addColumnDialogOpen}
        onClose={() => setAddColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar columna</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="add-column-form" onSubmit={submitAddColumnDialog}>
            <TextField
              fullWidth
              label="Nombre de columna"
              InputLabelProps={{ shrink: true }}
              inputRef={addColumnInputRef}
              autoFocus
              value={addColumnName}
              onChange={(event) => setAddColumnName(event.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setAddColumnDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="add-column-form"
            disabled={!canAddColumn || loading}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameColumnDialogOpen}
        onClose={() => setRenameColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renombrar columna</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="rename-column-form" onSubmit={submitRenameColumnDialog}>
            <Stack spacing={1.25}>
              <FormControl fullWidth disabled={headers.length === 0 || loading}>
                <Select
                  value={selectedColumnName}
                  sx={adminDropdownSx}
                  MenuProps={adminDropdownMenuProps}
                  displayEmpty
                  onChange={(event) => setSelectedColumnName(event.target.value)}
                >
                  <MenuItem value="">
                    <em>Seleccionar columna</em>
                  </MenuItem>
                  {visibleColumnManagementHeaders.map((header, index) => (
                    <MenuItem key={`${header || 'column'}-${index}`} value={header}>
                      {getColumnDisplayName(header, selectedSheet) || `Columna ${index}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nuevo nombre de columna"
                InputLabelProps={{ shrink: true }}
                value={newColumnName}
                onChange={(event) => setNewColumnName(event.target.value)}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setRenameColumnDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="rename-column-form"
            disabled={!canRenameColumn || loading}
          >
            Renombrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSheetDialogOpen}
        onClose={() => setDeleteSheetDialogOpen(false)}
      >
        <DialogTitle>Eliminar hoja</DialogTitle>
        <DialogContent dividers>
          <Typography>¿Eliminar hoja &quot;{getSheetDisplayName(selectedSheet)}&quot;?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setDeleteSheetDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            startIcon={<FileX2 size={16} />}
            onClick={confirmDeleteSheet}
            disabled={loading}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteColumnDialogOpen}
        onClose={() => setDeleteColumnDialogOpen(false)}
      >
        <DialogTitle>Eliminar columna</DialogTitle>
        <DialogContent dividers>
          <Typography>¿Eliminar columna &quot;{getColumnDisplayName(selectedColumnName, selectedSheet)}&quot;?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setDeleteColumnDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            startIcon={<Trash2 size={16} />}
            onClick={confirmDeleteColumn}
            disabled={loading}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteRowDialog.open}
        onClose={() => setDeleteRowDialog({ open: false, rowIndex: null, note: '', roleReplacementName: '' })}
      >
        <DialogTitle>Eliminar fila</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ minWidth: { xs: 260, sm: 420 } }}>
            <Typography>
              ¿Eliminar fila #
              {deleteRowDialog.rowIndex === null ? '' : deleteRowDialog.rowIndex + 1}?
            </Typography>
            {(isRolesSheet || isReferenceValueSheet) && (
              <>
                <Typography sx={{ color: 'text.secondary' }}>
                  {isRolesSheet
                    ? 'Si este rol ya está siendo usado en Usuarios o en solicitudes, puedes mover esos registros a otro rol o dejar el campo en blanco antes de eliminarlo.'
                    : `Si este valor ya está siendo usado en Usuarios o en solicitudes, puedes mover esos registros a otro valor de la hoja ${getSheetDisplayName(selectedSheet)} o dejar el campo en blanco antes de eliminarlo.`}
                </Typography>
                <TextField
                  select
                  sx={adminDropdownSx}
                  label={isRolesSheet ? 'Mover registros a otro rol' : 'Mover registros a otro valor'}
                  InputLabelProps={{ shrink: true }}
                  value={deleteRowDialog.roleReplacementName}
                  onChange={(event) => setDeleteRowDialog((previous) => ({
                    ...previous,
                    roleReplacementName: event.target.value,
                  }))}
                  SelectProps={{ displayEmpty: true, MenuProps: adminDropdownMenuProps }}
                  disabled={loading || replacementManagedValueOptions.length === 0}
                  helperText={
                    replacementManagedValueOptions.length === 0
                      ? 'No hay otros valores disponibles para mover los registros.'
                      : `${isRolesSheet ? 'Rol actual' : 'Valor actual'}: ${deletingManagedValue || 'Sin nombre'}`
                  }
                >
                  <MenuItem value="">
                    <em>Dejar referencias en blanco</em>
                  </MenuItem>
                  {replacementManagedValueOptions.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {isUsuariosSheet && (
              <TextField
                label="Nota"
                InputLabelProps={{ shrink: true }}
                value={deleteRowDialog.note}
                onChange={(event) => setDeleteRowDialog((previous) => ({
                  ...previous,
                  note: event.target.value,
                }))}
                multiline
                minRows={3}
                placeholder="Opcional"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            onClick={() => setDeleteRowDialog({ open: false, rowIndex: null, note: '', roleReplacementName: '' })}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            startIcon={<Trash2 size={16} />}
            onClick={confirmDeleteRow}
            disabled={loading}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={overrideDialog.open}
        onClose={closeOverrideDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sobrescribir solicitud existente</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography>{overrideDialog.message}</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              ¿Deseas sobrescribir la solicitud actual con la nueva información?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="text" onClick={closeOverrideDialog} disabled={overrideSubmitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={confirmOverrideDialog}
            disabled={overrideSubmitting}
          >
            {overrideSubmitting ? 'Sobrescribiendo...' : 'Sobrescribir solicitud'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={applyNewUserDialog.open}
        onClose={closeApplyNewUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Marcar solicitud como ejecutada</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography>
              Confirma que el usuario ya fue creado en las aplicaciones externas correspondientes. Esta acción solo registrará al usuario en la hoja de Usuarios y marcará la solicitud como ejecutada.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="text" onClick={closeApplyNewUserDialog} disabled={requestRowAction.mode === 'apply'}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={confirmApplyNewUserRequest}
            disabled={requestRowAction.mode === 'apply'}
          >
            Marcar como ejecutado
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminDashboard;
